import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Routes, Route, useSearchParams } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { InfoWindow } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { createClusterMarker } from './utils/clusterMarkerIcon';
import './App.css';
import {
  validateForm,
  validateField,
  getFieldValidationState,
  getCharacterCount,
  getFieldAriaAttributes,
} from './services/validation';
import AdminPanel from './AdminPanel';
import UserSettings from './components/UserSettings';
import ResetPasswordPage from './ResetPasswordPage';
import ImageEditor from './components/ImageEditor';
import ConfirmationModal from './components/ConfirmationModal';
import MapPicker from './components/MapPicker';
import RateLimitModal from './components/RateLimitModal';
import api from './services/api';
import StatisticsView from './components/StatisticsView';
import { useBrandingConfig } from './BrandingConfigContext';
import { useDesignTheme } from './design/DesignThemeContext';
import PublicWelcomeHeader from './components/PublicWelcomeHeader';
import CorridorStatsStrip from './components/CorridorStatsStrip';
import DesignThemeSwitcher from './components/DesignThemeSwitcher';
import {
  Mail,
  Lock,
  User as UserIcon,
  Settings,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react';
import { parseJsonArray } from './utils/parseJsonArray';
import INCIDENT_TYPES, { INCIDENT_TYPE_GROUPS } from './incidentTypes';
import AdminAnalytics from './components/AdminAnalytics';
import ExtraNotesManager from './components/ExtraNotesManager';
import useDefaultMapConfig from './hooks/useDefaultMapConfig';
import useUploadConfig from './hooks/useUploadConfig';
import useHeatmapConfig from './hooks/useHeatmapConfig';
import { resizeImagesIfNeeded } from './utils/imageResizer';
import { getMurrayRoadMapConfig } from './config/murrayRoadMapConfig';

import { createHeatmapOverlay } from './utils/canvasHeatmapOverlay';
import { reportsToHeatmapPoints } from './utils/heatmapUtils';

// Google Maps libraries (visualization/HeatmapLayer removed — deprecated in API 3.65+)
const GOOGLE_MAPS_LIBRARIES = [];

// Google Maps API Key validation
function getGoogleMapsApiKey() {
  // First try to get from window object (set by runtime environment)
  if (window.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return window.REACT_APP_GOOGLE_MAPS_API_KEY;
  }

  // Fallback to build-time environment variable
  if (process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  }

  // No fallback - throw error
  throw new Error(
    'REACT_APP_GOOGLE_MAPS_API_KEY environment variable is required. Please set it in your .env file.',
  );
}

const DEFAULT_CENTER = { lat: -37.7415, lng: 144.9796 }; // Murray Road center (Sydney Rd to Gilbert Rd)

function parseLatLng(str) {
  // Handle null, undefined, or empty values
  if (!str) return null;

  // If it's already an object with lat/lng, return it
  if (typeof str === 'object' && str !== null && str.lat !== undefined && str.lng !== undefined) {
    return str;
  }

  // If it's a string, parse it
  if (typeof str === 'string') {
    const match = str.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
    if (!match) return null;
    return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
  }

  // For any other type (number, boolean, etc.), return null
  return null;
}

function getMelbourneLocalDateTime() {
  // Get current time in Melbourne, Australia (Australia/Melbourne)
  const now = new Date();
  // Convert to Melbourne time zone string (YYYY-MM-DDTHH:mm)
  const melbourneTime = new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }));
  melbourneTime.setSeconds(0, 0);
  // Format as YYYY-MM-DDTHH:mm for datetime-local input
  const year = melbourneTime.getFullYear();
  const month = String(melbourneTime.getMonth() + 1).padStart(2, '0');
  const day = String(melbourneTime.getDate()).padStart(2, '0');
  const hour = String(melbourneTime.getHours()).padStart(2, '0');
  const minute = String(melbourneTime.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

// Helper function to format incident type for display
function formatIncidentType(incidentType) {
  if (!incidentType) return '';

  const typeMap = {
    // Motor Vehicles
    'car-collision': 'Car Collision (Car vs Car)',
    'multiple-vehicle-incident': 'Multiple Vehicle Incident',
    'car-pedestrian': 'Car vs Pedestrian',
    'car-bicycle': 'Car vs Bicycle',
    'car-motorcycle': 'Car vs Motorcycle',
    'car-animal': 'Car vs Animal',
    'car-property': 'Car vs Property',
    'car-rollover': 'Car Rollover',
    'car-fire': 'Car Fire',

    // Motorcycles
    'motorcycle-collision': 'Motorcycle Collision',
    'motorcycle-car': 'Motorcycle vs Car',
    'motorcycle-pedestrian': 'Motorcycle vs Pedestrian',
    'motorcycle-bicycle': 'Motorcycle vs Bicycle',
    'motorcycle-animal': 'Motorcycle vs Animal',
    'motorcycle-property': 'Motorcycle vs Property',
    'motorcycle-single': 'Single Motorcycle Accident',

    // Trucks
    'truck-collision': 'Truck Collision',
    'truck-car': 'Truck vs Car',
    'truck-motorcycle': 'Truck vs Motorcycle',
    'truck-bicycle': 'Truck vs Bicycle',
    'truck-pedestrian': 'Truck vs Pedestrian',
    'truck-animal': 'Truck vs Animal',
    'truck-property': 'Truck vs Property',
    'truck-rollover': 'Truck Rollover',
    'truck-load-spill': 'Truck Load Spill',

    // Bicycles
    'bicycle-collision': 'Bicycle Collision',
    'bicycle-car': 'Bicycle vs Car',
    'bicycle-motorcycle': 'Bicycle vs Motorcycle',
    'bicycle-truck': 'Bicycle vs Truck',
    'bicycle-pedestrian': 'Bicycle vs Pedestrian',
    'bicycle-animal': 'Bicycle vs Animal',
    'bicycle-property': 'Bicycle vs Property',
    'bicycle-single': 'Single Bicycle Accident',

    // Pedestrians
    'pedestrian-car': 'Pedestrian vs Car',
    'pedestrian-motorcycle': 'Pedestrian vs Motorcycle',
    'pedestrian-truck': 'Pedestrian vs Truck',
    'pedestrian-bicycle': 'Pedestrian vs Bicycle',
    'pedestrian-animal': 'Pedestrian vs Animal',
    'pedestrian-fall': 'Pedestrian Fall/Slip',
    'pedestrian-property': 'Pedestrian vs Property',

    // Animals
    'animal-car': 'Animal vs Car',
    'animal-motorcycle': 'Animal vs Motorcycle',
    'animal-truck': 'Animal vs Truck',
    'animal-bicycle': 'Animal vs Bicycle',
    'animal-pedestrian': 'Animal vs Pedestrian',
    'animal-loose': 'Loose Animal',

    // Near Misses & Dangerous Driving
    'near-miss-car': 'Near Miss - Car',
    'near-miss-motorcycle': 'Near Miss - Motorcycle',
    'near-miss-truck': 'Near Miss - Truck',
    'near-miss-bicycle': 'Near Miss - Bicycle',
    'near-miss-pedestrian': 'Near Miss - Pedestrian',
    'dangerous-driving-car': 'Dangerous Driving - Car',
    'dangerous-driving-motorcycle': 'Dangerous Driving - Motorcycle',
    'dangerous-driving-truck': 'Dangerous Driving - Truck',
    'dangerous-driving-bicycle': 'Dangerous Driving - Bicycle',
    'road-rage': 'Road Rage Incident',
    speeding: 'Excessive Speeding',
    'reckless-driving': 'Reckless Driving',

    // Road & Traffic Hazards
    'traffic-light-malfunction': 'Traffic-light Malfunction',
    'traffic-light-damage': 'Traffic-light Damage',
    'road-hazard': 'Road Hazard',
    'debris-on-road': 'Debris on Road',
    'pothole-or-surface-damage': 'Pothole or Surface Damage',
    'signage-issue': 'Signage Issue',
    'roadwork-issue': 'Roadwork Issue',
    'traffic-congestion-or-gridlock': 'Traffic Congestion/Gridlock',
    'other-traffic-hazard': 'Other Traffic Hazard',

    // Other
    'property-damage': 'Property Damage Only',
    fire: 'Fire Incident',
    flooding: 'Flooding/Water Damage',
    debris: 'Debris on Road',
    'traffic-light': 'Traffic Light Malfunction (Legacy)',
    other: 'Other/Unspecified',
  };

  return typeMap[incidentType] || incidentType;
}

function getReportTimeSortValue(report) {
  const isRange = report.isDateRange === true || report.isDateRange === 'true';
  const raw = isRange
    ? report.incidentDateRangeStart || report.incidentDateRangeEnd
    : report.estimatedIncidentTime;
  if (!raw) return 0;
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function getReportSortValue(report, column) {
  switch (column) {
    case 'ref':
      return Number(report.id) || 0;
    case 'type':
      return formatIncidentType(report.incidentType);
    case 'time':
      return getReportTimeSortValue(report);
    default:
      return 0;
  }
}

function SortableColumnHeader({ column, label, className, testId, sort, onSort }) {
  const isActive = sort.column === column;
  const ariaSort = isActive ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none';

  return (
    <th scope="col" className={className} data-testid={testId} aria-sort={ariaSort}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 -ml-1 px-1 py-1 sm:py-0.5 min-h-[36px] sm:min-h-0 rounded text-left font-medium whitespace-nowrap text-inherit hover:bg-gray-200/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 touch-manipulation"
        aria-label={
          isActive
            ? `Sort by ${label}, currently ${sort.direction === 'asc' ? 'ascending' : 'descending'}. Activate to reverse order.`
            : `Sort by ${label}`
        }
      >
        <span>{label}</span>
        {isActive ? (
          sort.direction === 'asc' ? (
            <ChevronUp className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          )
        ) : (
          <ChevronsUpDown className="w-3.5 h-3.5 shrink-0 text-gray-400" aria-hidden="true" />
        )}
      </button>
    </th>
  );
}

// Helper function to format time for display - Always use Melbourne timezone
function formatTimeForDisplay(
  timeString,
  timeUnknown = false,
  isDateRange = false,
  rangeStart = null,
  rangeEnd = null,
) {
  // Handle date ranges first
  if (isDateRange === true || isDateRange === 'true') {
    if (rangeStart && rangeEnd) {
      try {
        const startDate = new Date(rangeStart);
        const endDate = new Date(rangeEnd);
        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          const options = {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            timeZone: 'Australia/Melbourne',
          };
          return `Between ${startDate.toLocaleDateString('en-AU', options)} - ${endDate.toLocaleDateString('en-AU', options)}`;
        }
      } catch {
        // Fall through to default handling
      }
    }
    return 'Date range (unknown)';
  }

  if (!timeString) return '—';

  try {
    const date = new Date(timeString);
    if (isNaN(date.getTime())) return timeString;

    // If time is unknown, show date-only format with tilde prefix
    if (timeUnknown === true || timeUnknown === 'true') {
      const options = {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'Australia/Melbourne',
      };
      return `~${date.toLocaleDateString('en-AU', options)}`;
    }

    // Format as "12 Jul 2025, 2:30 PM" in Melbourne timezone
    const options = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Australia/Melbourne',
    };

    return date.toLocaleDateString('en-AU', options);
  } catch (error) {
    return timeString;
  }
}

// Helper function to anonymize reporter information for public display
function getAnonymizedReporter(user = null, isAdmin = false, reportData = null) {
  // Admins can see reporter details, public users see anonymized version
  if (isAdmin && reportData && reportData.reporterName) {
    return reportData.reporterName;
  }
  return 'Verified SWARM Reporter';
}

// Helper function to safely get file URL (handles both old and new formats)
function getSafeFileUrl(photoUrl) {
  if (!photoUrl) return null;

  // If it's already a full URL, return as is
  if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
    return photoUrl;
  }

  // If it starts with /uploads/, it's the old format, extract filename
  if (photoUrl.startsWith('/uploads/')) {
    const filename = photoUrl.replace('/uploads/', '');
    const url = api.getFileUrl(filename);
    console.log('getSafeFileUrl - old format:', { photoUrl, filename, url });
    return url;
  }

  // Otherwise, assume it's just a filename
  const url = api.getFileUrl(photoUrl);
  console.log('getSafeFileUrl - filename:', { photoUrl, url });
  return url;
}

// Helper function to generate incident type icon as SVG data URL for placeholders
function getIncidentTypeIconUrl(incidentType, size = 80) {
  // Define colors for different incident categories
  const colors = {
    // Motor Vehicles - Red
    car: { fill: '#ef4444', stroke: '#dc2626' },
    // Motorcycles - Orange
    motorcycle: { fill: '#f97316', stroke: '#ea580c' },
    // Scooters - Teal
    scooter: { fill: '#14b8a6', stroke: '#0d9488' },
    // Trucks - Purple
    truck: { fill: '#8b5cf6', stroke: '#7c3aed' },
    // Bicycles - Blue
    bicycle: { fill: '#3b82f6', stroke: '#2563eb' },
    // Pedestrians - Green
    pedestrian: { fill: '#10b981', stroke: '#059669' },
    // Animals - Brown
    animal: { fill: '#a16207', stroke: '#92400e' },
    // Near Misses - Yellow
    nearMiss: { fill: '#eab308', stroke: '#ca8a04' },
    // Dangerous Driving - Dark Red
    dangerous: { fill: '#dc2626', stroke: '#b91c1c' },
    // Other - Gray
    other: { fill: '#6b7280', stroke: '#4b5563' },
  };

  // Determine color based on incident type
  let color = colors.other;
  if (incidentType) {
    // Prioritize bicycle and pedestrian colors for safety awareness
    if (incidentType.includes('bicycle')) {
      color = colors.bicycle;
    } else if (incidentType.includes('pedestrian')) {
      color = colors.pedestrian;
    } else if (incidentType.includes('car') || incidentType.includes('vehicle')) {
      color = colors.car;
    } else if (incidentType.includes('motorcycle')) {
      color = colors.motorcycle;
    } else if (incidentType.includes('scooter')) {
      color = colors.scooter;
    } else if (incidentType.includes('truck')) {
      color = colors.truck;
    } else if (incidentType.includes('animal')) {
      color = colors.animal;
    } else if (incidentType.includes('near-miss')) {
      color = colors.nearMiss;
    } else if (
      incidentType.includes('dangerous') ||
      incidentType.includes('speeding') ||
      incidentType.includes('reckless') ||
      incidentType.includes('road-rage')
    ) {
      color = colors.dangerous;
    }
  }

  // Generate SVG with appropriate icon based on incident type
  let iconPath = '';
  let iconColor = '#ffffff';

  if (incidentType) {
    // Prioritize bicycle and pedestrian icons for safety awareness
    if (incidentType.includes('bicycle')) {
      iconPath =
        'M15.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM5 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5zm5.8-10l2.4-2.4.8.8c1.3 1.3 3 2.1 5.1 2.1V9c-1.5 0-2.7-.6-3.6-1.5l-1.9-1.9c-.5-.4-1-.6-1.6-.6s-1.1.2-1.4.6L7.8 8.4c-.4.4-.6.9-.6 1.4 0 .6.2 1.1.6 1.4L11 14v5h2v-6.2l-2.2-2.3zM19 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5z';
    } else if (incidentType.includes('pedestrian')) {
      iconPath =
        'M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm9 7h-6v13h-2v-6h-2v6H9V9H3V7h18v2z';
    } else if (incidentType.includes('car') || incidentType.includes('vehicle')) {
      iconPath =
        'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z';
    } else if (incidentType.includes('motorcycle')) {
      iconPath =
        'M19.44 9.03L15.41 5H11v2h3.59l2 2H5c-2.8 0-5 2.2-5 5s2.2 5 5 5c2.46 0 4.45-1.69 4.9-4h.65c.45 2.31 2.44 4 4.9 4 2.8 0 5-2.2 5-5 0-2.65-2.15-4.8-4.56-4.97zM7.82 15C7.4 16.15 6.28 17 5 17c-1.66 0-3-1.34-3-3s1.34-3 3-3c1.28 0 2.4.85 2.82 2H7.82zM19 17c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z';
    } else if (incidentType.includes('scooter')) {
      iconPath =
        'M12 2C8.13 2 5 5.13 5 9c0 1.74.69 3.32 1.81 4.48L7 15h10l.19-1.52C18.31 12.32 19 10.74 19 9c0-3.87-3.13-7-7-7zM8.5 9c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5zm7 0c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5zM12 17c-1.1 0-2-.9-2-2h4c0 1.1-.9 2-2 2z';
    } else if (incidentType.includes('truck')) {
      iconPath =
        'M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h4c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z';
    } else if (incidentType.includes('animal')) {
      iconPath = 'M4.5 9.5m-2.5 0a2.5 2.5 0 1 1 5 0a2.5 2.5 0 1 1 -5 0';
      iconPath += 'M19.5 9.5m-2.5 0a2.5 2.5 0 1 1 5 0a2.5 2.5 0 1 1 -5 0';
      iconPath += 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z';
    } else if (incidentType.includes('near-miss')) {
      iconPath =
        'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z';
    } else if (
      incidentType.includes('dangerous') ||
      incidentType.includes('speeding') ||
      incidentType.includes('reckless') ||
      incidentType.includes('road-rage')
    ) {
      iconPath =
        'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z';
    } else if (incidentType.includes('fire')) {
      iconPath =
        'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zM12 11c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z';
    } else {
      // Default warning triangle for other incidents
      iconPath = 'M12 2L1 21h22L12 2zm0 3.17L19.83 19H4.17L12 5.17zM11 16h2v2h-2zm0-6h2v4h-2z';
      iconColor = '#ffffff';
    }
  } else {
    // Default warning triangle for unknown incidents
    iconPath = 'M12 2L1 21h22L12 2zm0 3.17L19.83 19H4.17L12 5.17zM11 16h2v2h-2zm0-6h2v4h-2z';
    iconColor = '#ffffff';
  }

  // Create SVG with background circle and icon
  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.2)"/>
        </filter>
      </defs>
      <!-- Background circle -->
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 4}" fill="${color.fill}" stroke="${color.stroke}" stroke-width="2" filter="url(#shadow)"/>
      <!-- Icon -->
      <path d="${iconPath}" fill="${iconColor}" transform="translate(${size / 2 - 12}, ${size / 2 - 12}) scale(1.5)"/>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// Helper function to generate custom marker icons based on incident type
function getMarkerIcon(incidentType, isActive = false) {
  // Make markers smaller to reduce crowding
  const baseSize = isActive ? 32 : 28;
  const totalHeight = baseSize + 8; // Add extra height for the point
  const anchorPoint = baseSize / 2;

  // Define colors for different incident categories
  const colors = {
    // Motor Vehicles - Red
    car: { fill: '#ef4444', stroke: '#dc2626' },
    // Motorcycles - Orange
    motorcycle: { fill: '#f97316', stroke: '#ea580c' },
    // Scooters - Teal
    scooter: { fill: '#14b8a6', stroke: '#0d9488' },
    // Trucks - Purple
    truck: { fill: '#8b5cf6', stroke: '#7c3aed' },
    // Bicycles - Blue
    bicycle: { fill: '#3b82f6', stroke: '#2563eb' },
    // Pedestrians - Green
    pedestrian: { fill: '#10b981', stroke: '#059669' },
    // Animals - Brown
    animal: { fill: '#a16207', stroke: '#92400e' },
    // Near Misses - Yellow
    nearMiss: { fill: '#eab308', stroke: '#ca8a04' },
    // Dangerous Driving - Dark Red
    dangerous: { fill: '#dc2626', stroke: '#b91c1c' },
    // Other - Gray
    other: { fill: '#6b7280', stroke: '#4b5563' },
  };

  // Determine color based on incident type
  let color = colors.other;
  if (incidentType) {
    // Prioritize bicycle and pedestrian colors for safety awareness
    if (incidentType.includes('bicycle')) {
      color = colors.bicycle;
    } else if (incidentType.includes('pedestrian')) {
      color = colors.pedestrian;
    } else if (incidentType.includes('car') || incidentType.includes('vehicle')) {
      color = colors.car;
    } else if (incidentType.includes('motorcycle')) {
      color = colors.motorcycle;
    } else if (incidentType.includes('scooter')) {
      color = colors.scooter;
    } else if (incidentType.includes('truck')) {
      color = colors.truck;
    } else if (incidentType.includes('animal')) {
      color = colors.animal;
    } else if (incidentType.includes('near-miss')) {
      color = colors.nearMiss;
    } else if (
      incidentType.includes('dangerous') ||
      incidentType.includes('speeding') ||
      incidentType.includes('reckless') ||
      incidentType.includes('road-rage')
    ) {
      color = colors.dangerous;
    }
  }

  // Generate SVG with appropriate icon based on incident type
  let iconPath = '';
  let iconColor = '#ffffff';

  if (incidentType) {
    // Prioritize bicycle and pedestrian icons for safety awareness
    if (incidentType.includes('bicycle')) {
      iconPath =
        'M15.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM5 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5zm5.8-10l2.4-2.4.8.8c1.3 1.3 3 2.1 5.1 2.1V9c-1.5 0-2.7-.6-3.6-1.5l-1.9-1.9c-.5-.4-1-.6-1.6-.6s-1.1.2-1.4.6L7.8 8.4c-.4.4-.6.9-.6 1.4 0 .6.2 1.1.6 1.4L11 14v5h2v-6.2l-2.2-2.3zM19 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5z';
    } else if (incidentType.includes('pedestrian')) {
      iconPath =
        'M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm9 7h-6v13h-2v-6h-2v6H9V9H3V7h18v2z';
    } else if (incidentType.includes('car') || incidentType.includes('vehicle')) {
      iconPath =
        'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z';
    } else if (incidentType.includes('motorcycle')) {
      iconPath =
        'M19.44 9.03L15.41 5H11v2h3.59l2 2H5c-2.8 0-5 2.2-5 5s2.2 5 5 5c2.46 0 4.45-1.69 4.9-4h.65c.45 2.31 2.44 4 4.9 4 2.8 0 5-2.2 5-5 0-2.65-2.15-4.8-4.56-4.97zM7.82 15C7.4 16.15 6.28 17 5 17c-1.66 0-3-1.34-3-3s1.34-3 3-3c1.28 0 2.4.85 2.82 2H7.82zM19 17c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z';
    } else if (incidentType.includes('scooter')) {
      iconPath =
        'M12 2C8.13 2 5 5.13 5 9c0 1.74.69 3.32 1.81 4.48L7 15h10l.19-1.52C18.31 12.32 19 10.74 19 9c0-3.87-3.13-7-7-7zM8.5 9c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5zm7 0c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5zM12 17c-1.1 0-2-.9-2-2h4c0 1.1-.9 2-2 2z';
    } else if (incidentType.includes('truck')) {
      iconPath =
        'M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h4c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z';
    } else if (incidentType.includes('animal')) {
      iconPath = 'M4.5 9.5m-2.5 0a2.5 2.5 0 1 1 5 0a2.5 2.5 0 1 1 -5 0';
      iconPath += 'M19.5 9.5m-2.5 0a2.5 2.5 0 1 1 5 0a2.5 2.5 0 1 1 -5 0';
      iconPath += 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z';
    } else if (incidentType.includes('near-miss')) {
      iconPath =
        'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z';
    } else if (
      incidentType.includes('dangerous') ||
      incidentType.includes('speeding') ||
      incidentType.includes('reckless') ||
      incidentType.includes('road-rage')
    ) {
      iconPath =
        'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z';
    } else if (incidentType.includes('fire')) {
      iconPath =
        'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zM12 11c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z';
    } else {
      // Default warning triangle for other incidents
      iconPath = 'M12 2L1 21h22L12 2zm0 3.17L19.83 19H4.17L12 5.17zM11 16h2v2h-2zm0-6h2v4h-2z';
      iconColor = '#ffffff';
    }
  } else {
    // Default warning triangle for unknown incidents
    iconPath = 'M12 2L1 21h22L12 2zm0 3.17L19.83 19H4.17L12 5.17zM11 16h2v2h-2zm0-6h2v4h-2z';
    iconColor = '#ffffff';
  }

  // Create a teardrop-shaped marker with a longer, more pointed end
  const svg = `
    <svg width="${baseSize}" height="${totalHeight}" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.3)"/>
        </filter>
      </defs>
      <!-- Teardrop shape with longer point -->
      <path d="M12 2C6.48 2 2 6.48 2 12c0 5.52 4.48 10 10 10s10-4.48 10-10C22 6.48 17.52 2 12 2zM12 20L8 28L12 26L16 28L12 20z" 
            fill="${color.fill}" 
            filter="url(#shadow)"/>
      <path d="M12 4C7.58 4 4 7.58 4 12c0 4.42 3.58 8 8 8s8-3.58 8-8C20 7.58 16.42 4 12 4zM12 18L10 24L12 22L14 24L12 18z" 
            fill="${color.stroke}"/>
      <!-- Icon in the center -->
      <path d="${iconPath}" fill="${iconColor}" transform="translate(0, -2) scale(0.8)"/>
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(baseSize, totalHeight),
    anchor: new window.google.maps.Point(anchorPoint, totalHeight),
  };
}

// Note: User authentication is now handled by the backend API
// The default admin user is created automatically by the backend

function App() {
  return (
    <div className="min-h-screen bg-gray-50 swarm-app-shell" data-testid="app-shell">
      <Routes>
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/*" element={<MainApp />} />
      </Routes>
    </div>
  );
}

function MainApp() {
  console.log('MainApp component rendering');
  const detailViewRef = useRef(null);
  const errorSummaryRef = useRef(null);
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState({
    photos: [], // array of {file, caption}
    location: '', // human-readable address
    coordinates: '', // lat, lng
    description: '',
    emergencyServicesAttended: '', // '', 'yes', 'no'
    policeAttended: false,
    ambulanceAttended: false,
    fireBrigadeAttended: false,
    policeReportTime: '',
    policeOfficerName: '',
    policeReportNumber: '',
    reporterName: '',
    reporterNumber: '',
    reporterEmail: '',
    witnessedIncident: '', // '', 'yes', 'no', 'unsure', 'after', 'evidence'
    evidenceDescription: '', // description of evidence when witnessedIncident is 'evidence'
    estimatedIncidentTime: getMelbourneLocalDateTime(),
    timeUnknown: false,
    incidentDateRangeStart: '',
    incidentDateRangeEnd: '',
    isDateRange: false,
    injuries: '', // '', 'yes', 'no', 'unsure'
    injuryCount: '', // number of injuries if any
    vehiclesInvolved: '', // number of vehicles involved
    injuryNotes: '', // <-- new field
  });
  const [formErrors, setFormErrors] = useState({});
  const [formWarnings, setFormWarnings] = useState({});
  const [formSuggestions, setFormSuggestions] = useState({});
  const [fieldValidationStates, setFieldValidationStates] = useState({});
  const [locating, setLocating] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [draggedPhotoIdx, setDraggedPhotoIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [cropModal, setCropModal] = useState({ open: false, file: null, idx: null });
  const [imageEditor, setImageEditor] = useState({ open: false, file: null, idx: null });
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [aspect, setAspect] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [previewModal, setPreviewModal] = useState({
    open: false,
    url: '',
    caption: '',
    photos: [],
    currentIndex: 0,
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Debug: Log when fileInputRef is set
  useEffect(() => {
    console.log('fileInputRef.current changed:', fileInputRef.current);
  }, [fileInputRef.current]);
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [stickyMarkerId, setStickyMarkerId] = useState(null);
  const [selectedReportForDetails, setSelectedReportForDetails] = useState(null);
  const [hoveredReportForDetails, setHoveredReportForDetails] = useState(null);
  const [pendingReports, setPendingReports] = useState([]);
  const [user, setUser] = useState(null); // { name, email, number }
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAdminPanel, setShowAdminPanel] = useState(() => {
    // Check if we're in admin mode based on URL
    return searchParams.get('tab') !== null;
  });
  const [showStatistics, setShowStatistics] = useState(() => {
    // Check if we're in statistics mode based on URL
    return searchParams.get('view') === 'statistics';
  });
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserSettings, setShowUserSettings] = useState(false);

  // Sync URL changes with admin panel and statistics state
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const viewFromUrl = searchParams.get('view');
    const shouldShowAdmin = tabFromUrl !== null;
    const shouldShowStatistics = viewFromUrl === 'statistics';

    if (shouldShowAdmin !== showAdminPanel) {
      setShowAdminPanel(shouldShowAdmin);
    }
    if (shouldShowStatistics !== showStatistics) {
      setShowStatistics(shouldShowStatistics);
    }
  }, [searchParams, showAdminPanel, showStatistics]);
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'register' | 'forgot-password'
  const [authForm, setAuthForm] = useState({
    name: '',
    email: '',
    number: '',
    password: '',
    error: '',
    success: '',
  });
  const [twoFactorAuth, setTwoFactorAuth] = useState({
    isRequired: false,
    tempToken: '',
    token: '',
    error: '',
  });
  const [editingReport, setEditingReport] = useState(null); // report being edited
  const [editImages, setEditImages] = useState([]); // images for edit modal
  const [editImageFiles, setEditImageFiles] = useState([]); // new files for edit modal
  const [editImageCaptions, setEditImageCaptions] = useState([]); // captions for edit modal
  const [profileEdit, setProfileEdit] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    number: '',
    password: '',
    error: '',
    success: '',
  });
  const [userList, setUserList] = useState([]);

  const [alertRecipients, setAlertRecipients] = useState({ emails: [], phones: [] });
  const [userAlertPreferences, setUserAlertPreferences] = useState({});
  const [adminRedirected, setAdminRedirected] = useState(() => {
    return sessionStorage.getItem('adminRedirected') === 'true';
  });
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertContent, setAlertContent] = useState('');
  const [emails, setEmails] = useState('');
  const [phones, setPhones] = useState('');

  // Load alert preferences and recipients
  const loadAlertData = async () => {
    try {
      console.log('📧 Loading alert data...');
      const [preferencesResponse, recipientsResponse] = await Promise.all([
        api.getUserAlertPreferences(),
        api.getAlertRecipients(),
      ]);

      // Handle the API response format
      const preferences = preferencesResponse.preferences || preferencesResponse;
      const recipients = recipientsResponse.recipients || recipientsResponse;

      console.log('📧 Loaded preferences:', preferences);
      console.log('📧 Loaded recipients:', recipients);
      setUserAlertPreferences(preferences);
      setAlertRecipients(recipients);
    } catch (error) {
      console.error('Error loading alert data:', error);
    }
  };

  // Demo alert function
  const showDemoAlert = message => {
    setAlertContent(message);
    setShowAlertModal(true);
  };
  const [showMapPopup, setShowMapPopup] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showMapPicker, setShowMapPicker] = useState(false);

  // Default map configuration hook
  const { defaultCenter, defaultZoom, mapRestrictions } = useDefaultMapConfig();

  // Upload configuration hook (for dynamic photo limits)
  const { config: uploadConfig } = useUploadConfig();
  const maxPhotos = uploadConfig?.maxPhotos ?? 6;
  const maxFileSizeMB = uploadConfig?.maxFileSizeMB ?? 2;

  // Heatmap configuration hook
  const { config: heatmapConfig, loading: heatmapConfigLoading } = useHeatmapConfig();
  const heatmapDefaultsApplied = useRef(false);

  // Map state for zoom and center
  const [mapZoom, setMapZoom] = useState(defaultZoom || 14);
  const [mapCenter, setMapCenter] = useState(defaultCenter || DEFAULT_CENTER);

  // Map options and picker marker
  const [mapOptions] = useState({
    zoomControl: true,
    mapTypeControl: false,
    scaleControl: true,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: true,
    disableDefaultUi: false,
  });
  const [mapPickerMarker, setMapPickerMarker] = useState(null);
  const [isResettingMap, setIsResettingMap] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showHeatmapClusters, setShowHeatmapClusters] = useState(true);
  const [heatmapRadius, setHeatmapRadius] = useState(40);
  const heatmapRef = useRef(null);
  const clustererRef = useRef(null);

  useEffect(() => {
    if (heatmapConfigLoading || heatmapDefaultsApplied.current) return;
    setHeatmapRadius(heatmapConfig.defaultRadius);
    setShowHeatmapClusters(heatmapConfig.showClustersDefault);
    heatmapDefaultsApplied.current = true;
  }, [heatmapConfigLoading, heatmapConfig]);

  // Active area config state
  const [activeArea, setActiveArea] = useState(null);

  const [currentPage, setCurrentPage] = useState('main'); // 'main' | 'admin-reports'
  const [showReportForm, setShowReportForm] = useState(false); // 'main' | 'admin-reports'
  const [isAdminMode, setIsAdminMode] = useState(false); // for admin report creation
  const [timeFilter, setTimeFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isInitializing, setIsInitializing] = useState(true);
  const [editingUser, setEditingUser] = useState(null); // user being edited in admin panel
  const [userEditForm, setUserEditForm] = useState({
    name: '',
    email: '',
    number: '',
    password: '',
    isAdmin: false,
    error: '',
    success: '',
  });

  const [announcement, setAnnouncement] = useState('');
  const mainContentRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const tableContainerRef = useRef(null);
  const [pendingScrollReportId, setPendingScrollReportId] = useState(null);

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    confirmButtonClass: 'btn btn-error',
    cancelButtonClass: 'btn btn-secondary',
  });

  // Rate limit state
  const [rateLimitModal, setRateLimitModal] = useState({
    isOpen: false,
    retryInSeconds: 0,
  });

  const [userTab, setUserTab] = useState('main'); // 'main' | 'statistics'

  // Pagination state for recent incidents table
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const [itemsPerPage] = useState(10); // Fixed items per page
  const [reportSort, setReportSort] = useState({ column: 'time', direction: 'desc' });

  const [showEditModal, setShowEditModal] = useState(false); // controls visibility of edit modal

  const { brandingConfig } = useBrandingConfig();
  const { copy, isCorridor } = useDesignTheme();

  const [successMessage, setSuccessMessage] = useState('');

  const [showAuthModal, setShowAuthModal] = useState(false);

  // Success modal state for report submission feedback
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedReportId, setSubmittedReportId] = useState(null);

  // Function to close all modals
  const closeAllModals = () => {
    setShowAuthModal(false);
    setShowReportForm(false);
    setShowMapPicker(false);
    setShowMapPopup(false);
    setShowEditModal(false);
    setShowAdminPanel(false);
    // Note: showStatistics is now managed by URL, not by closeAllModals
    setShowAlertModal(false);
    setShowSuccessModal(false);
    setAuthMode('login');
  };

  const openStatisticsView = () => {
    closeAllModals();
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('view', 'statistics');
    setSearchParams(newSearchParams);
  };

  // Function to fetch active area config
  const fetchActiveArea = useCallback(async () => {
    try {
      const response = await api.getAreaConfig();
      if (response.activeArea) {
        setActiveArea(response.activeArea);
        // Set map center/zoom if available
        if (response.activeArea.center_lat && response.activeArea.center_lng) {
          setMapCenter({
            lat: response.activeArea.center_lat,
            lng: response.activeArea.center_lng,
          });
        }
        if (response.activeArea.zoom_level) {
          setMapZoom(response.activeArea.zoom_level);
        }
      } else {
        // Fallback to default if no active area
        setActiveArea(null);
        setMapCenter(defaultCenter || DEFAULT_CENTER);
        setMapZoom(defaultZoom || 16);
      }
    } catch (err) {
      console.error('Error fetching active area config:', err);
      // Fallback to default
      setActiveArea(null);
      setMapCenter(defaultCenter || DEFAULT_CENTER);
      setMapZoom(defaultZoom || 16);
    }
  }, [defaultCenter, defaultZoom]);

  // Update map when default values change (if no active area is set)
  useEffect(() => {
    console.log(
      '🔍 App: useEffect triggered - defaultCenter:',
      defaultCenter,
      'defaultZoom:',
      defaultZoom,
      'activeArea:',
      activeArea,
    );
    if (!activeArea && (defaultCenter || defaultZoom)) {
      const newCenter = defaultCenter || DEFAULT_CENTER;
      const newZoom = defaultZoom || 16;
      console.log('🔍 App: Setting map center to:', newCenter, 'zoom to:', newZoom);
      setMapCenter(newCenter);
      setMapZoom(newZoom);

      // Also update the map directly if it's already loaded
      if (mapRef.current) {
        console.log('🔍 App: Updating map directly via mapRef');
        mapRef.current.panTo(newCenter);
        mapRef.current.setZoom(newZoom);
      }
    }
  }, [defaultCenter, defaultZoom, activeArea]);

  // Helper function to check if a report is rejected (not just pending)
  // A rejected report has isApproved=false AND approvedBy is set (someone explicitly rejected it)
  const isReportRejected = useCallback(report => {
    return !report.isApproved && report.approvedBy;
  }, []);

  // Filter reports based on time, type, and user permissions
  const filteredReports = useMemo(() => {
    // Ensure reports is always an array
    if (!Array.isArray(reports)) {
      console.warn('Reports is not an array:', reports);
      return [];
    }

    return reports.filter(report => {
      // Always exclude rejected reports from the public view and map
      // Rejected reports should only be visible in admin management areas
      if (isReportRejected(report)) {
        return false;
      }

      // Time filter
      if (timeFilter !== 'all') {
        const reportDate = new Date(report.estimatedIncidentTime);
        const now = new Date();

        switch (timeFilter) {
          case 'today':
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            if (reportDate < today) return false;
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            if (reportDate < weekAgo) return false;
            break;
          case 'month':
            const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
            if (reportDate < monthAgo) return false;
            break;
          case 'year':
            const yearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
            if (reportDate < yearAgo) return false;
            break;
          default:
            break;
        }
      }

      // Type filter
      if (typeFilter !== 'all' && report.incidentType !== typeFilter) {
        return false;
      }

      // User permission filter - normal users can only see approved reports or their own pending reports
      if (user && !user.isAdmin) {
        // For normal users: show approved reports OR their own pending reports
        if (!report.isApproved && report.userEmail !== user.email) {
          return false;
        }
      } else if (user && user.isAdmin) {
        // For admins: show all reports (both approved and pending) in the main table
        // No filtering needed - admins can see everything (except rejected, handled above)
      } else if (!user) {
        // For public users (no authentication): only show approved reports
        if (!report.isApproved) {
          return false;
        }
      }

      return true;
    });
  }, [reports, timeFilter, typeFilter, user, isReportRejected]);

  const incidentTypesWithData = useMemo(() => {
    if (!Array.isArray(reports)) return [];
    const types = new Set();
    reports.forEach(report => {
      if (!report?.incidentType) return;
      if (isReportRejected(report)) return;
      if (user && !user.isAdmin) {
        if (!report.isApproved && report.userEmail !== user.email) return;
      } else if (!user && !report.isApproved) {
        return;
      }
      types.add(report.incidentType);
    });
    return [...types].sort((a, b) =>
      formatIncidentType(a).localeCompare(formatIncidentType(b), 'en', { sensitivity: 'base' }),
    );
  }, [reports, user, isReportRejected]);

  useEffect(() => {
    if (typeFilter !== 'all' && !incidentTypesWithData.includes(typeFilter)) {
      setTypeFilter('all');
    }
  }, [typeFilter, incidentTypesWithData]);

  // Update map center when filtered reports change (but only if no active area is set)
  useEffect(() => {
    console.log(
      '🔍 Map centering useEffect: filteredReports changed, count:',
      filteredReports.length,
      'activeArea:',
      activeArea,
    );
    if (!activeArea) {
      // Use database default center and zoom, fallback to hardcoded values
      const centerToUse = defaultCenter || DEFAULT_CENTER;
      const zoomToUse = defaultZoom || 15;
      console.log(
        '🔍 Map centering useEffect: Using default center from database:',
        centerToUse,
        'zoom:',
        zoomToUse,
      );
      setMapCenter(centerToUse);
      setMapZoom(zoomToUse);
    }
  }, [filteredReports, activeArea, defaultCenter, defaultZoom]);

  // Update map when mapRef becomes available and we have default config
  useEffect(() => {
    if (mapRef.current && !activeArea && (defaultCenter || defaultZoom)) {
      const newCenter = defaultCenter || DEFAULT_CENTER;
      const newZoom = defaultZoom || 16;
      mapRef.current.panTo(newCenter);
      mapRef.current.setZoom(newZoom);
    }
  }, [mapRef.current, defaultCenter, defaultZoom, activeArea]);

  // Announce changes to screen readers
  const announceToScreenReader = message => {
    setAnnouncement(message);
    setTimeout(() => setAnnouncement(''), 1000);
  };

  // Global error handler for rate limits
  const handleRateLimitError = error => {
    if (
      error.message.toLowerCase().includes('rate limit') ||
      error.message.toLowerCase().includes('too many requests')
    ) {
      setRateLimitModal({
        isOpen: true,
        retryInSeconds: 0,
      });
    }
  };

  // Confirmation modal helpers
  const showConfirmationModal = (title, message, onConfirm, options = {}) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      onConfirm,
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      confirmButtonClass: options.confirmButtonClass || 'btn btn-error',
      cancelButtonClass: options.cancelButtonClass || 'btn btn-secondary',
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false }));
  };
  //stats focus
  useEffect(() => {
    if (userTab === 'statistics') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [userTab]);
  // Focus management for keyboard navigation
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.focus();
    }
  }, [currentPage]);

  // Debug: Monitor currentPage changes
  useEffect(() => {
    console.log('App: currentPage changed to:', currentPage);
  }, [currentPage]);

  // Suppress Google Maps Marker deprecation warnings
  useEffect(() => {
    const originalWarn = console.warn;
    console.warn = (...args) => {
      if (
        args[0] &&
        typeof args[0] === 'string' &&
        args[0].includes('google.maps.Marker is deprecated')
      ) {
        return; // Suppress this specific warning
      }
      originalWarn.apply(console, args);
    };

    return () => {
      console.warn = originalWarn;
    };
  }, []);

  // Google Maps loader
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: getGoogleMapsApiKey(),
    // Suppress deprecation warnings for now until @react-google-maps/api supports AdvancedMarkerElement
    version: 'weekly',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Memoize markers for performance
  const reportMarkers = useMemo(() => {
    // Helper to check if rejected (inline since we can't use hooks in useMemo callback)
    const isRejected = report => !report.isApproved && report.approvedBy;

    const filteredReports = reports.filter(report => {
      // Always exclude rejected reports from the map
      if (isRejected(report)) {
        return false;
      }

      // Apply the same filtering logic as filteredReports
      // Time filter
      if (timeFilter !== 'all') {
        const reportDate = new Date(report.estimatedIncidentTime);
        const now = new Date();

        switch (timeFilter) {
          case 'today':
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            if (reportDate < today) return false;
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            if (reportDate < weekAgo) return false;
            break;
          case 'month':
            const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
            if (reportDate < monthAgo) return false;
            break;
          case 'year':
            const yearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
            if (reportDate < yearAgo) return false;
            break;
          default:
            break;
        }
      }

      // Type filter
      if (typeFilter !== 'all' && report.incidentType !== typeFilter) {
        return false;
      }

      // User permission filter - normal users can only see approved reports or their own pending reports
      if (user && !user.isAdmin) {
        // For normal users: show approved reports OR their own pending reports
        if (!report.isApproved && report.userEmail !== user.email) {
          return false;
        }
      } else if (user && user.isAdmin) {
        // For admins: show approved and pending reports on the map (rejected excluded above)
        // No additional filtering needed
      } else if (!user) {
        // For public users (no authentication): only show approved reports
        if (!report.isApproved) {
          return false;
        }
      }

      return true;
    });

    const markers = filteredReports
      .map((r, i) => {
        const photoUrl = r.photoUrls && r.photoUrls[0] ? getSafeFileUrl(r.photoUrls[0]) : null;

        // Handle coordinates - could be string (old format) or object (new format)
        let coords = null;
        if (typeof r.coordinates === 'string') {
          coords = parseLatLng(r.coordinates);
        } else if (
          r.coordinates &&
          typeof r.coordinates === 'object' &&
          r.coordinates.lat &&
          r.coordinates.lng
        ) {
          coords = { lat: r.coordinates.lat, lng: r.coordinates.lng };
        } else if (r.lat && r.lng) {
          // Fallback to direct lat/lng properties
          coords = { lat: r.lat, lng: r.lng };
        }

        return {
          ...coords,
          id: r.id,
          label: `${i + 1}`,
          description: r.description,
          reporterName: getAnonymizedReporter(user, user?.isAdmin, r), // Anonymize reporter name
          originalReporterName: r.reporterName, // Keep original for getAnonymizedReporter function
          photoUrl,
          photoUrls: Array.isArray(r.photoUrls) ? r.photoUrls : [], // Ensure photoUrls is always an array
          photoCaptions: parseJsonArray(r.photoCaptions),
          time: r.policeReportTime,
          estimatedIncidentTime: r.estimatedIncidentTime, // Add missing field
          timeUnknown: r.timeUnknown,
          isDateRange: r.isDateRange,
          incidentDateRangeStart: r.incidentDateRangeStart,
          incidentDateRangeEnd: r.incidentDateRangeEnd,
          location: r.location,
          incidentType: r.incidentType,
          // Add emergency services fields for incident details
          emergencyServicesAttended: r.emergencyServicesAttended,
          policeAttended: r.policeAttended,
          ambulanceAttended: r.ambulanceAttended,
          fireBrigadeAttended: r.fireBrigadeAttended,
          policeReportTime: r.policeReportTime,
          policeOfficerName: r.policeOfficerName,
          policeReportNumber: r.policeReportNumber,
          submitterIp: r.submitterIp,
        };
      })
      .filter(m => m.lat && m.lng);

    return markers;
  }, [reports, timeFilter, typeFilter, user]);

  // Canvas heatmap overlay (replaces deprecated google.maps.visualization.HeatmapLayer)
  useEffect(() => {
    if (!mapRef.current || !isLoaded) return undefined;

    if (showHeatmap) {
      const heatmapPoints = reportsToHeatmapPoints(filteredReports);

      if (heatmapPoints.length > 0) {
        try {
          if (!heatmapRef.current) {
            heatmapRef.current = createHeatmapOverlay(mapRef.current, {
              points: heatmapPoints,
              radius: heatmapRadius,
              opacity: heatmapConfig.opacity,
              gradient: heatmapConfig.gradient,
            });
          } else {
            heatmapRef.current.setData(heatmapPoints);
            heatmapRef.current.setRadius(heatmapRadius);
            heatmapRef.current.setOpacity(heatmapConfig.opacity);
            heatmapRef.current.setGradient(heatmapConfig.gradient);
          }
        } catch (error) {
          console.error('Error creating heatmap:', error);
        }
      } else if (heatmapRef.current) {
        heatmapRef.current.setMap(null);
        heatmapRef.current = null;
      }
    } else if (heatmapRef.current) {
      heatmapRef.current.setMap(null);
      heatmapRef.current = null;
    }

    return () => {
      if (heatmapRef.current) {
        heatmapRef.current.setMap(null);
        heatmapRef.current = null;
      }
    };
  }, [showHeatmap, filteredReports, heatmapRadius, isLoaded, heatmapConfig]);

  // Manage marker clustering when in heatmap mode
  useEffect(() => {
    if (!mapRef.current || !isLoaded) return;

    if (showHeatmap && showHeatmapClusters && reportMarkers.length > 0) {
      // Create markers for clustering
      const markers = reportMarkers.map(marker => {
        const googleMarker = new window.google.maps.Marker({
          position: { lat: marker.lat, lng: marker.lng },
          icon: getMarkerIcon(
            marker.incidentType,
            activeMarkerId === marker.id || stickyMarkerId === marker.id,
          ),
          title: `SWARM-${marker.id}`,
        });

        // Add click listeners
        googleMarker.addListener('click', () => {
          const isSame = stickyMarkerId === marker.id;
          setStickyMarkerId(isSame ? null : marker.id);
          setSelectedReportForDetails(isSame ? null : marker);
          setActiveMarkerId(isSame ? null : marker.id);
        });

        return googleMarker;
      });

      // Create clusterer
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
      }

      clustererRef.current = new MarkerClusterer({
        map: mapRef.current,
        markers,
        renderer: {
          render: ({ count, position }) => createClusterMarker(count, position),
        },
      });
    } else if (clustererRef.current) {
      // Clear clusterer when not in heatmap mode
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }

    // Cleanup on unmount
    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current = null;
      }
    };
  }, [showHeatmap, showHeatmapClusters, reportMarkers, isLoaded, activeMarkerId, stickyMarkerId]);

  // Helper function to check if a report has a corresponding marker
  const hasMarker = useMemo(() => {
    const markerIds = new Set(reportMarkers.map(m => m.id));
    return reportId => markerIds.has(reportId);
  }, [reportMarkers]);

  const currentMarker = useMemo(() => {
    // Handle coordinates - could be string (old format) or object (new format)
    if (typeof form.coordinates === 'string') {
      return parseLatLng(form.coordinates);
    } else if (
      form.coordinates &&
      typeof form.coordinates === 'object' &&
      form.coordinates.lat &&
      form.coordinates.lng
    ) {
      return { lat: form.coordinates.lat, lng: form.coordinates.lng };
    }
    return null;
  }, [form.coordinates]);

  // Center map on a marker and apply a top padding so the InfoWindow stays visible
  const centerMapWithTopPadding = useCallback((latitude, longitude, paddingPx = 140) => {
    // Validate coordinates before proceeding
    if (
      typeof latitude !== 'number' ||
      typeof longitude !== 'number' ||
      isNaN(latitude) ||
      isNaN(longitude)
    ) {
      console.warn('Invalid coordinates provided to centerMapWithTopPadding:', {
        latitude,
        longitude,
      });
      return;
    }

    try {
      const map = mapRef.current;
      if (map && window.google && window.google.maps) {
        const target = new window.google.maps.LatLng(latitude, longitude);
        if (typeof map.panTo === 'function') {
          map.panTo(target);
        }
        // Nudge the map content down so InfoWindow (which opens above) has room
        if (typeof map.panBy === 'function') {
          setTimeout(() => {
            try {
              map.panBy(0, -paddingPx);
            } catch (e) {}
          }, 0);
        }
      }
    } catch (_) {
      // no-op
    }
    setMapCenter({ lat: latitude, lng: longitude });
  }, []);

  const selectReportFromTable = useCallback(
    report => {
      const marker = reportMarkers.find(m => m.id === report.id);
      const hasMapPin =
        marker &&
        marker.lat &&
        marker.lng &&
        typeof marker.lat === 'number' &&
        typeof marker.lng === 'number';

      const details = hasMapPin
        ? marker
        : {
            ...report,
            photoUrl: report.photoUrls?.[0] ? getSafeFileUrl(report.photoUrls[0]) : null,
            photoUrls: parseJsonArray(report.photoUrls),
            photoCaptions: parseJsonArray(report.photoCaptions),
          };

      setSelectedReportForDetails(details);

      if (hasMapPin) {
        setActiveMarkerId(marker.id);
        setStickyMarkerId(marker.id);
        centerMapWithTopPadding(marker.lat, marker.lng);
        if (
          mapContainerRef.current &&
          typeof mapContainerRef.current.scrollIntoView === 'function'
        ) {
          mapContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        setActiveMarkerId(null);
        setStickyMarkerId(null);
        if (detailViewRef.current && typeof detailViewRef.current.scrollIntoView === 'function') {
          detailViewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    },
    [reportMarkers, centerMapWithTopPadding],
  );

  // InfoWindow options
  const getInfoWindowOptions = () => ({
    closeBoxURL: '',
    disableAutoPan: false,
    pixelOffset: new window.google.maps.Size(0, -30),
    closeBoxMargin: '0px',
    enableEventPropagation: true,
  });

  // Hover handlers for table rows only with debouncing
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [isHoveringOverMap, setIsHoveringOverMap] = useState(false);
  const [currentlyHoveredRow, setCurrentlyHoveredRow] = useState(null);

  // Marker hover debouncing to prevent flashing
  const [markerHoverTimeout, setMarkerHoverTimeout] = useState(null);
  const [isHoveringMarker, setIsHoveringMarker] = useState(false);
  const [lastHoverTime, setLastHoverTime] = useState(0);

  const handleTableRowMouseOver = useCallback(
    (markerId, lat, lng) => {
      if (stickyMarkerId === markerId) return;

      // Clear any existing timeout
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }

      // Track the currently hovered row
      setCurrentlyHoveredRow(markerId);

      // Set active marker immediately for visual feedback
      setActiveMarkerId(markerId);

      // Only change map if not already at this location
      const currentCenter = mapCenter;
      const currentZoom = mapZoom;

      if (currentCenter.lat !== lat || currentCenter.lng !== lng) {
        centerMapWithTopPadding(lat, lng);
        // Don't reset zoom - preserve current zoom level
      }
    },
    [stickyMarkerId, mapCenter, mapZoom, hoverTimeout, centerMapWithTopPadding],
  );

  const handleTableRowMouseOut = useCallback(
    markerId => {
      if (stickyMarkerId === markerId) return;

      // Clear any existing timeout
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }

      // Clear the currently hovered row
      setCurrentlyHoveredRow(null);

      // Set a longer timeout to clear the active marker to prevent infoWindow from closing too quickly
      const timeout = setTimeout(() => {
        // Only clear the active marker if:
        // 1. User is not hovering over the map
        // 2. No other row is currently being hovered
        if (!isHoveringOverMap && currentlyHoveredRow === null) {
          setActiveMarkerId(null);

          // Only reset map if no sticky marker is active
          if (stickyMarkerId === null) {
            if (activeArea && activeArea.center_lat && activeArea.center_lng) {
              setMapCenter({
                lat: activeArea.center_lat,
                lng: activeArea.center_lng,
              });
              setMapZoom(activeArea.zoom_level || 16);
            } else {
              setMapCenter(DEFAULT_CENTER);
              setMapZoom(16);
            }
          }
        }
      }, 500); // Increased from 100ms to 500ms for better user experience

      setHoverTimeout(timeout);
    },
    [stickyMarkerId, hoverTimeout, isHoveringOverMap, currentlyHoveredRow, activeArea],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
      if (markerHoverTimeout) {
        clearTimeout(markerHoverTimeout);
      }
      setCurrentlyHoveredRow(null);
      setIsHoveringOverMap(false);
      setIsHoveringMarker(false);
    };
  }, [hoverTimeout, markerHoverTimeout]);

  // Debounced marker hover handlers to prevent flashing
  const handleMarkerMouseOver = useCallback(
    markerId => {
      const now = Date.now();

      // Throttle rapid mouse events - ignore if less than 50ms since last hover
      if (now - lastHoverTime < 50) {
        return;
      }

      setLastHoverTime(now);

      // Clear any existing timeout
      if (markerHoverTimeout) {
        clearTimeout(markerHoverTimeout);
      }

      setIsHoveringMarker(true);
      setActiveMarkerId(markerId);
    },
    [markerHoverTimeout, lastHoverTime],
  );

  const handleMarkerMouseOut = useCallback(
    markerId => {
      // Clear any existing timeout
      if (markerHoverTimeout) {
        clearTimeout(markerHoverTimeout);
      }

      setIsHoveringMarker(false);

      // Set a timeout to clear the active marker after a delay
      const timeout = setTimeout(() => {
        if (!stickyMarkerId || stickyMarkerId !== markerId) {
          setActiveMarkerId(null);
        }
      }, 300); // Increased delay to 300ms for better stability

      setMarkerHoverTimeout(timeout);
    },
    [markerHoverTimeout, stickyMarkerId],
  );

  // Function to shorten address for infoWindow display
  const shortenAddress = useCallback(fullAddress => {
    if (!fullAddress) return '';

    // Extract just the street number and street name (first two comma-separated parts)
    const parts = fullAddress.split(',');

    if (parts.length >= 2) {
      // Take first two parts: street number and street name
      const streetNumber = parts[0].trim();
      const streetName = parts[1].trim();
      return `${streetNumber}, ${streetName}`;
    } else if (parts.length === 1) {
      // If no commas, return the whole thing
      return parts[0].trim();
    }

    return fullAddress;
  }, []);

  // Reset map hover state when sticky marker changes
  useEffect(() => {
    if (stickyMarkerId) {
      setIsHoveringOverMap(false);
      setCurrentlyHoveredRow(null);
    }
  }, [stickyMarkerId]);

  const handleReportSort = useCallback(column => {
    setReportSort(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: column === 'type' ? 'asc' : 'desc' };
    });
  }, []);

  const sortedReports = useMemo(() => {
    const { column, direction } = reportSort;
    const multiplier = direction === 'asc' ? 1 : -1;
    return [...filteredReports].sort((a, b) => {
      const aVal = getReportSortValue(a, column);
      const bVal = getReportSortValue(b, column);
      let cmp = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        cmp = aVal.localeCompare(bVal, 'en', { sensitivity: 'base' });
      } else if (aVal < bVal) {
        cmp = -1;
      } else if (aVal > bVal) {
        cmp = 1;
      }
      if (cmp === 0) {
        cmp = (Number(a.id) || 0) - (Number(b.id) || 0);
      }
      return cmp * multiplier;
    });
  }, [filteredReports, reportSort]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedReports.length / itemsPerPage);
  const startIndex = (currentTablePage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReports = sortedReports.slice(startIndex, endIndex);

  // Reset to first page when filters or sort change
  useEffect(() => {
    setCurrentTablePage(1);
  }, [timeFilter, typeFilter, reportSort]);

  // After changing table page due to InfoWindow click, scroll the target row into view
  useEffect(() => {
    if (!pendingScrollReportId || !tableContainerRef.current) return;
    // Wait for the DOM to render the new page rows
    const timeout = setTimeout(() => {
      const row = tableContainerRef.current.querySelector(
        `tr[data-report-id="${pendingScrollReportId}"]`,
      );
      if (row && typeof row.scrollIntoView === 'function') {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setPendingScrollReportId(null);
    }, 50);
    return () => clearTimeout(timeout);
  }, [currentTablePage, pendingScrollReportId]);

  const isDuplicate = (file, files) => {
    return files.some(f => f.file?.name === file.name && f.file?.size === file.size);
  };

  const getDefaultVehiclesInvolved = incidentType => {
    if (!incidentType) return null;
    const defaults = {
      'car-collision': 2,
      'dangerous-driving-car': 1,
      'near-miss-car': 1,
      'bicycle-car': 2,
      'pedestrian-car': 2,
      'multiple-vehicle-incident': 3,
    };
    return defaults[incidentType] ?? null;
  };

  const getVehiclesInvolvedDisplay = report => {
    if (!report) return null;
    if (
      report.vehiclesInvolved !== null &&
      report.vehiclesInvolved !== undefined &&
      report.vehiclesInvolved !== ''
    ) {
      return { value: report.vehiclesInvolved, isDefault: false };
    }
    const defaultVehicles = getDefaultVehiclesInvolved(report.incidentType);
    if (defaultVehicles !== null) {
      return { value: defaultVehicles, isDefault: true };
    }
    return null;
  };

  useEffect(() => {
    if (!form.incidentType) return;
    if (
      form.vehiclesInvolved !== '' &&
      form.vehiclesInvolved !== null &&
      form.vehiclesInvolved !== undefined
    ) {
      return;
    }
    const defaultVehicles = getDefaultVehiclesInvolved(form.incidentType);
    if (defaultVehicles !== null) {
      setForm(prev => ({ ...prev, vehiclesInvolved: defaultVehicles }));
    }
  }, [form.incidentType]);

  const validateAndAddPhotos = async newFiles => {
    const errors = [];
    const currentPhotos = [...form.photos];
    const { maxPhotos, maxFileSizeMB, enableClientResize } = uploadConfig;

    console.log('🚨 PHOTO UPLOAD DEBUG START!');
    console.log('validateAndAddPhotos: Current photos count:', currentPhotos.length);
    console.log('validateAndAddPhotos: New files count:', newFiles.length);
    console.log('validateAndAddPhotos: Upload config:', {
      maxPhotos,
      maxFileSizeMB,
      enableClientResize,
    });
    console.log(
      'validateAndAddPhotos: Current photos structure:',
      currentPhotos.map((p, idx) => ({
        index: idx,
        hasFile: !!p.file,
        hasUrl: !!p.url,
        fileName: p.file?.name,
        url: p.url,
      })),
    );

    if (!newFiles || newFiles.length === 0) {
      setPhotoError('No files were selected. Please choose at least one image.');
      return;
    }

    if (currentPhotos.length >= maxPhotos) {
      setPhotoError(
        `You have reached the ${maxPhotos} photo limit. Remove a photo to add another.`,
      );
      return;
    }

    // Filter to only valid image files, collect errors for unsupported files
    const imageFiles = [];
    newFiles.forEach(file => {
      if (!file || !file.name) {
        errors.push('One of the selected files is invalid. Please try again.');
        return;
      }
      if (!file.type || !file.type.startsWith('image/')) {
        const typeLabel = file.type ? ` (${file.type})` : '';
        errors.push(`"${file.name}" is not a supported image type${typeLabel}.`);
        return;
      }
      if (file.size === 0) {
        errors.push(`"${file.name}" appears to be empty. Please choose a different file.`);
        return;
      }
      imageFiles.push(file);
    });

    if (imageFiles.length === 0) {
      setPhotoError(errors.join(' '));
      return;
    }

    // Resize images if enabled and needed
    let processedFiles = imageFiles;
    if (enableClientResize && imageFiles.length > 0) {
      console.log('validateAndAddPhotos: Resizing images if needed...');
      try {
        const resizeResult = await resizeImagesIfNeeded(imageFiles, uploadConfig);
        processedFiles =
          resizeResult.files && resizeResult.files.length > 0 ? resizeResult.files : imageFiles;
        if (resizeResult.resizedCount > 0) {
          console.log(
            `validateAndAddPhotos: Resized ${resizeResult.resizedCount} images, saved ${(resizeResult.totalSaved / 1024 / 1024).toFixed(2)}MB`,
          );
        }
      } catch (resizeError) {
        console.warn('validateAndAddPhotos: Resize failed, using original files', resizeError);
        errors.push('We could not resize one or more images. The original files were kept.');
        processedFiles = imageFiles;
      }
    }

    for (const file of processedFiles) {
      if (currentPhotos.length >= maxPhotos) {
        errors.push(`You can only upload up to ${maxPhotos} photos per report.`);
        break;
      }
      if (isDuplicate(file, currentPhotos)) {
        errors.push(`"${file.name}" is already added. Remove the duplicate to continue.`);
        continue;
      }
      if (file.size > maxFileSizeMB * 1024 * 1024) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
        errors.push(
          `File "${file.name}" is ${fileSizeMB}MB, which exceeds the ${maxFileSizeMB}MB limit.${enableClientResize ? ' Auto-resize was attempted but the file is still too large.' : ''} Please use a smaller image or reduce quality manually.`,
        );
        continue;
      }

      // Add new file to the end of the photos array
      currentPhotos.push({ file, caption: '', url: null });
      console.log(
        'validateAndAddPhotos: Added file:',
        file.name,
        'Total photos now:',
        currentPhotos.length,
      );
    }

    console.log(
      'validateAndAddPhotos: Final photos array:',
      currentPhotos.map((p, idx) => ({
        index: idx,
        hasFile: !!p.file,
        hasUrl: !!p.url,
        fileName: p.file?.name,
        url: p.url,
      })),
    );

    console.log('🚨 PHOTO UPLOAD DEBUG END - Setting form state');
    setForm(prev => ({ ...prev, photos: currentPhotos }));
    setPhotoError(errors.length > 0 ? errors.join(' ') : '');
    console.log('🚨 PHOTO UPLOAD DEBUG COMPLETE!');
  };

  const handleChange = e => {
    const { name, value, files } = e.target;
    console.log('handleChange called with name:', name);
    if (name === 'photos') {
      console.log('Photos selected:', files ? files.length : 0, 'files');
      validateAndAddPhotos(Array.from(files));

      // Clear the file input to allow selecting the same files again if needed
      e.target.value = '';
    } else {
      setForm(prev => ({ ...prev, [name]: value }));

      // Real-time validation for text fields
      if (name !== 'photos') {
        validateFieldRealTime(name, value);
      }
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setDragOverIdx(null);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndAddPhotos(Array.from(e.dataTransfer.files));
      e.dataTransfer.clearData();
    }
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
    if (typeof idx === 'number') setDragOverIdx(idx);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setDragOverIdx(null);
  };

  const handleClickDropArea = () => {
    console.log('handleClickDropArea called');
    console.log('fileInputRef.current:', fileInputRef.current);
    if (fileInputRef.current) {
      console.log('Clicking file input...');
      fileInputRef.current.click();
    } else {
      console.log('fileInputRef.current is null');
    }
  };

  // Drag-to-reorder handlers
  const handlePhotoDragStart = idx => setDraggedPhotoIdx(idx);
  const handlePhotoDragOver = idx => {
    if (draggedPhotoIdx === null || draggedPhotoIdx === idx) return;
    setForm(prev => {
      const photos = [...prev.photos];
      const [dragged] = photos.splice(draggedPhotoIdx, 1);
      photos.splice(idx, 0, dragged);
      return { ...prev, photos };
    });
    setDraggedPhotoIdx(idx);
    setDragOverIdx(idx);
  };
  const handlePhotoDragEnd = () => {
    setDraggedPhotoIdx(null);
    setDragOverIdx(null);
  };

  const handleUseLocation = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async position => {
        const { latitude, longitude } = position.coords;
        const coords = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        let address = coords;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`,
          );
          if (response.ok) {
            const data = await response.json();
            address = data.display_name || coords;
          }
        } catch (err) {
          // fallback to coords
        }
        setForm(prev => ({
          ...prev,
          location: address,
          coordinates: coords,
        }));
        setLocating(false);
      },
      error => {
        alert('Unable to retrieve your location.');
        setLocating(false);
      },
    );
  };

  const openMapPopup = () => {
    setShowMapPicker(true);
    setSelectedLocation(null);
  };

  const closeMapPopup = () => {
    setShowMapPicker(false);
    setSelectedLocation(null);
  };

  const handleMapPickerConfirm = picked => {
    setForm(prev => ({
      ...prev,
      location: picked.address,
      coordinates: picked.coords,
    }));
    setShowMapPicker(false);
  };

  // Function to reset map to default center and zoom
  const resetMapToDefault = () => {
    setIsResettingMap(true);

    const targetCenter =
      activeArea && activeArea.center_lat && activeArea.center_lng
        ? { lat: activeArea.center_lat, lng: activeArea.center_lng }
        : defaultCenter || DEFAULT_CENTER;
    const targetZoom =
      activeArea && activeArea.zoom_level ? activeArea.zoom_level : defaultZoom || 14;

    setMapCenter(targetCenter);
    setMapZoom(targetZoom);

    // Reset to marker view (not heatmap)
    setShowHeatmap(false);

    // Close any open info windows
    setSelectedReportForDetails(null);
    setStickyMarkerId(null);
    setActiveMarkerId(null);

    // If map is loaded, smoothly pan to the target location
    if (mapRef.current) {
      mapRef.current.panTo(targetCenter);
      mapRef.current.setZoom(targetZoom);
    }

    // Reset loading state after a short delay to show the animation
    setTimeout(() => {
      setIsResettingMap(false);
      announceToScreenReader('Map reset to default view');
    }, 500);
  };

  const handleMapClick = async event => {
    // Prevent any default behavior and stop propagation when available
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    } else if (event && event.domEvent && typeof event.domEvent.stopPropagation === 'function') {
      event.domEvent.stopPropagation();
    }

    // Ensure we have valid coordinates
    if (!event || !event.latLng) {
      console.warn('No latLng in map click event');
      return;
    }

    // Ensure we get numeric values from the Google Maps event
    const lat = typeof event.latLng.lat === 'function' ? event.latLng.lat() : event.latLng.lat;
    const lng = typeof event.latLng.lng === 'function' ? event.latLng.lng() : event.latLng.lng;

    // Validate coordinates
    if (isNaN(lat) || isNaN(lng)) {
      console.warn('Invalid coordinates received:', { lat, lng });
      return;
    }

    // Convert to numbers to ensure toFixed works
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);

    const coords = `${latNum.toFixed(6)}, ${lngNum.toFixed(6)}`;

    // Get address from coordinates
    let address = coords;
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latNum}&lon=${lngNum}`,
      );
      if (response.ok) {
        const data = await response.json();
        address = data.display_name || coords;
      }
    } catch (err) {
      console.warn('Failed to get address for coordinates:', err);
    }

    setSelectedLocation({ lat: latNum, lng: lngNum, address, coords });
  };

  const confirmLocation = () => {
    if (selectedLocation) {
      setForm(prev => ({
        ...prev,
        location: selectedLocation.address,
        coordinates: selectedLocation.coords,
      }));
      closeMapPopup();
    }
  };

  // Note: simulateUpload function removed - now using real API uploads

  const validateFormData = () => {
    const errors = {};

    // Date range validation
    if (form.isDateRange) {
      if (!form.incidentDateRangeStart) {
        errors.incidentDateRangeStart = 'Start date is required for date ranges';
      }
      if (!form.incidentDateRangeEnd) {
        errors.incidentDateRangeEnd = 'End date is required for date ranges';
      }

      if (form.incidentDateRangeStart && form.incidentDateRangeEnd) {
        const startDate = new Date(form.incidentDateRangeStart);
        const endDate = new Date(form.incidentDateRangeEnd);

        if (endDate <= startDate) {
          errors.incidentDateRangeEnd = 'End date must be after start date';
        }

        // Check maximum range of 5 days
        const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
        if (daysDiff > 5) {
          errors.incidentDateRangeEnd = 'Date range cannot exceed 5 days';
        }
      }
    }

    const result = validateForm(form, isAdminMode);
    const allErrors = { ...errors, ...result.errors };
    setFormErrors(allErrors);
    setFormWarnings(result.warnings);
    setFormSuggestions(result.suggestions);
    return allErrors;
  };

  // Real-time field validation
  const validateFieldRealTime = (fieldName, value) => {
    const result = validateField(fieldName, value, form);
    const validationState = getFieldValidationState(fieldName, value, form);

    setFieldValidationStates(prev => ({
      ...prev,
      [fieldName]: validationState,
    }));

    if (!result.isValid) {
      setFormErrors(prev => ({
        ...prev,
        [fieldName]: result.message,
      }));
    } else {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }

    if (result.suggestions) {
      setFormSuggestions(prev => ({
        ...prev,
        [fieldName]: result.suggestions,
      }));
    }

    return result;
  };

  // Helper function to render validation messages
  const renderValidationMessage = fieldName => {
    const error = formErrors[fieldName];
    const warning = formWarnings[fieldName];
    const suggestions = formSuggestions[fieldName];
    const validationState = fieldValidationStates[fieldName];

    if (error) {
      return (
        <div className={'validation-message error'} id={`${fieldName}-error`}>
          {error}
          {suggestions && suggestions.length > 0 && (
            <div className="mt-2">
              <strong>Helpful suggestions:</strong>
              <ul className="mt-1 ml-4 list-disc">
                {suggestions.map((suggestion, idx) => (
                  <li key={idx} className="text-sm">
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }

    if (warning) {
      return (
        <div className={'validation-message warning'} id={`${fieldName}-warning`}>
          {warning}
          {suggestions && suggestions.length > 0 && (
            <div className="mt-2">
              <strong>Helpful suggestions:</strong>
              <ul className="mt-1 ml-4 list-disc">
                {suggestions.map((suggestion, idx) => (
                  <li key={idx} className="text-sm">
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }

    if (suggestions && suggestions.length > 0 && validationState === 'success') {
      return (
        <div className={'validation-message info'} id={`${fieldName}-info`}>
          <strong>Helpful suggestions:</strong>
          <ul className="mt-1 ml-4 list-disc">
            {suggestions.map((suggestion, idx) => (
              <li key={idx} className="text-sm">
                {suggestion}
              </li>
            ))}
          </ul>
        </div>
      );
    }

    return null;
  };

  // Helper function to get field CSS classes
  const getFieldClasses = fieldName => {
    const validationState = fieldValidationStates[fieldName];
    const baseClasses =
      'form-input w-full px-3 py-3 border border-gray-300 rounded-lg text-sm bg-white text-gray-700';

    switch (validationState) {
      case 'error':
        return `${baseClasses} error`;
      case 'success':
        return `${baseClasses} success`;
      case 'warning':
        return `${baseClasses} warning`;
      default:
        return baseClasses;
    }
  };

  // Helper function to get field help text
  const getFieldHelpText = fieldName => {
    const suggestions = formSuggestions[fieldName];
    if (suggestions && suggestions.length > 0) {
      return (
        <div className="field-help" id={`${fieldName}-help`}>
          {suggestions[0]}
        </div>
      );
    }
    return null;
  };

  // When editingReport is set, pre-fill the form with the report's data
  useEffect(() => {
    if (editingReport) {
      setForm({
        photos: (Array.isArray(editingReport.photoUrls)
          ? editingReport.photoUrls
          : editingReport.photoUrl
            ? [editingReport.photoUrl]
            : []
        ).map((url, idx) => ({
          file: null,
          url,
          caption: parseJsonArray(editingReport.photoCaptions)[idx] || '',
        })),
        location: editingReport.location || '',
        coordinates: editingReport.coordinates || '',
        description: editingReport.description || '',
        emergencyServicesAttended: editingReport.emergencyServicesAttended || '',
        policeAttended: editingReport.policeAttended || false,
        ambulanceAttended: editingReport.ambulanceAttended || false,
        fireBrigadeAttended: editingReport.fireBrigadeAttended || false,
        policeReportTime: editingReport.policeReportTime || '',
        policeOfficerName: editingReport.policeOfficerName || '',
        policeReportNumber: editingReport.policeReportNumber || '',
        reporterName: editingReport.reporterName || '',
        reporterNumber: editingReport.reporterNumber || '',
        reporterEmail: editingReport.reporterEmail || '',
        witnessedIncident: editingReport.witnessedIncident || '',
        estimatedIncidentTime: editingReport.estimatedIncidentTime || getMelbourneLocalDateTime(),
        injuries: editingReport.injuries || '',
        injuryCount: editingReport.injuryCount || '',
        vehiclesInvolved:
          editingReport.vehiclesInvolved ||
          getDefaultVehiclesInvolved(editingReport.incidentType) ||
          '',
        injuryNotes: editingReport.injuryNotes || '',
        incidentType: editingReport.incidentType || '',
      });
      setShowReportForm(true);
    }
  }, [editingReport]);

  // On close of the report form modal, clear editingReport
  const handleCloseReportForm = () => {
    closeAllModals();
    setEditingReport(null);
  };

  // On submit, update the report if editing, otherwise create new
  const handleSubmit = async e => {
    e.preventDefault();
    setPhotoError('');
    const errors = validateFormData();
    if (Object.keys(errors).length > 0) {
      // Scroll to error summary
      if (errorSummaryRef.current) {
        errorSummaryRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        // Focus on error summary for screen readers
        setTimeout(() => {
          errorSummaryRef.current.focus();
        }, 500);
      }
      return;
    }

    try {
      let photoUrls = [];
      let photoCaptions = [];

      // Upload photos if any
      if (form.photos.length > 0) {
        setUploading(true);

        try {
          // Build photoUrls array maintaining original order
          photoUrls = [];
          const filesToUpload = [];

          console.log('🔍 App Submit - Processing photos:', form.photos.length);
          console.log(
            '🔍 App Submit - Form photos structure:',
            form.photos.map((p, idx) => ({
              index: idx,
              hasFile: !!p.file,
              hasUrl: !!p.url,
              fileName: p.file?.name,
              url: p.url,
              caption: p.caption,
            })),
          );

          // Process each photo in order
          form.photos.forEach((photo, index) => {
            if (photo.file) {
              // New file - add placeholder and track file for upload
              photoUrls.push('UPLOAD_PLACEHOLDER');
              filesToUpload.push(photo.file);
              console.log(`🔍 App Submit - Photo ${index}: New file ${photo.file.name}`);
            } else if (photo.url) {
              // Existing URL - keep as is
              photoUrls.push(photo.url);
              console.log(`🔍 App Submit - Photo ${index}: Existing URL ${photo.url}`);
            } else {
              // This shouldn't happen, but log it for debugging
              console.log(`🔍 App Submit - Photo ${index}: No file or URL`, photo);
            }
          });

          console.log('🔍 App Submit - Files to upload:', filesToUpload.length);

          // Upload files and replace placeholders
          if (filesToUpload.length > 0) {
            const uploadResponse = await api.uploadPhotos(filesToUpload, progress => {
              setUploadProgress(progress);
            });

            if (!uploadResponse || !Array.isArray(uploadResponse.files)) {
              throw new Error('Upload completed but no files were returned by the server.');
            }

            if (uploadResponse.files.length !== filesToUpload.length) {
              throw new Error(
                'Some photos failed to upload. Please retry or remove the problematic images.',
              );
            }

            const newPhotoUrls = uploadResponse.files.map(f => f.filename);

            // Replace placeholders with actual URLs
            let urlIndex = 0;
            for (let i = 0; i < photoUrls.length; i++) {
              if (photoUrls[i] === 'UPLOAD_PLACEHOLDER') {
                photoUrls[i] = newPhotoUrls[urlIndex];
                urlIndex++;
              }
            }

            console.log('🔍 App Submit - Final photoUrls:', photoUrls);
          }

          photoCaptions = form.photos.map(p => p.caption);
        } catch (uploadError) {
          console.error('Photo upload failed:', uploadError);
          const message =
            uploadError && uploadError.message
              ? uploadError.message
              : 'Photo upload failed. Please try again.';
          setPhotoError(
            `Photo upload failed: ${message} You can remove the problematic photo and try again.`,
          );
          throw uploadError;
        } finally {
          setUploading(false);
          setUploadProgress(0);
        }
      }

      // Prepare report data
      const reportData = {
        ...form,
        photoUrls,
        photoCaptions,
      };

      if (editingReport) {
        // Update existing report
        await api.updateReport(editingReport.id, reportData);
        setReports(prev =>
          prev.map(r => (r.id === editingReport.id ? { ...r, ...reportData } : r)),
        );
        announceToScreenReader('Report updated successfully');
        // Keep modal open and update editingReport to the updated report
        const updated = { ...editingReport, ...reportData };
        setEditingReport(updated);
        setFormErrors({});
        setSuccessMessage('Report updated successfully!');
        setTimeout(() => setSuccessMessage(''), 2000);
      } else {
        // Create new report
        const response = await api.createReport(reportData);
        setReports(prev => [response.report, ...prev]);
        announceToScreenReader('Report submitted successfully');
        // Show success modal with feedback
        setSubmittedReportId(response.report.id);
        setShowSuccessModal(true);
        // Reset form but keep modal open until user closes success modal
        setForm({
          photos: [],
          location: '',
          coordinates: '',
          description: '',
          emergencyServicesAttended: '',
          policeAttended: false,
          ambulanceAttended: false,
          fireBrigadeAttended: false,
          policeReportTime: '',
          policeOfficerName: '',
          policeReportNumber: '',
          reporterName: '',
          reporterNumber: '',
          reporterEmail: '',
          witnessedIncident: '',
          evidenceDescription: '',
          estimatedIncidentTime: getMelbourneLocalDateTime(),
          injuries: '',
          injuryCount: '',
          vehiclesInvolved: '',
          injuryNotes: '',
        });
        setFormErrors({});
        setEditingReport(null);
      }
    } catch (error) {
      console.error('Failed to submit report:', error);
      setFormErrors({ submit: error.message });
      setUploading(false);
      setUploadProgress(0);

      // Scroll to error summary for API errors
      if (errorSummaryRef.current) {
        errorSummaryRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        // Focus on error summary for screen readers
        setTimeout(() => {
          errorSummaryRef.current.focus();
        }, 500);
      }
    }
  };

  const handleRemovePhoto = idx => {
    setForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== idx),
    }));
    setPhotoError('');
  };

  const movePhoto = (idx, direction) => {
    setForm(prev => {
      const photos = [...prev.photos];
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= photos.length) return prev;
      [photos[idx], photos[newIdx]] = [photos[newIdx], photos[idx]];
      return { ...prev, photos };
    });
  };

  const handleCaptionChange = (idx, value) => {
    setForm(prev => {
      const photos = [...prev.photos];
      photos[idx] = { ...photos[idx], caption: value };
      return { ...prev, photos };
    });
  };

  // Advanced cropping modal using react-easy-crop
  const openCropModal = (file, idx) => {
    setCropModal({ open: true, file, idx });
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAspect(1);
    setCroppedAreaPixels(null);
    setRotation(0);
  };
  const closeCropModal = () => setCropModal({ open: false, file: null, idx: null });
  const onCropChange = c => setCrop(c);
  const onZoomChange = z => setZoom(z);
  const onAspectChange = e => setAspect(Number(e.target.value));
  const onCropComplete = (croppedArea, croppedAreaPixels) =>
    setCroppedAreaPixels(croppedAreaPixels);
  const onRotationChange = delta => setRotation(r => (r + delta + 360) % 360);

  // Utility to crop image using canvas
  async function getCroppedImg(imageSrc, cropPixels, rotation = 0) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Unable to access canvas context for cropping.');
    }
    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));
    canvas.width = safeArea;
    canvas.height = safeArea;
    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-safeArea / 2, -safeArea / 2);
    ctx.drawImage(image, (safeArea - image.width) / 2, (safeArea - image.height) / 2);
    const data = ctx.getImageData(0, 0, safeArea, safeArea);
    canvas.width = cropPixels.width;
    canvas.height = cropPixels.height;
    ctx.putImageData(
      data,
      Math.round(0 - safeArea / 2 + image.width / 2 - cropPixels.x),
      Math.round(0 - safeArea / 2 + image.height / 2 - cropPixels.y),
    );
    return new Promise(resolve => {
      canvas.toBlob(blob => {
        resolve(blob);
      }, 'image/jpeg');
    });
  }
  function createImage(url) {
    return new Promise((resolve, reject) => {
      const image = new window.Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });
  }

  const handleCrop = async () => {
    if (!cropModal.file || cropModal.idx === null || !croppedAreaPixels) return;
    const imageSrc = URL.createObjectURL(cropModal.file);
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      if (!croppedBlob) {
        setPhotoError('Image cropping failed. Please try again with a different image.');
        return;
      }
      const croppedFile = new File([croppedBlob], form.photos[cropModal.idx].file.name, {
        type: croppedBlob.type,
      });
      setForm(prev => {
        const photos = [...prev.photos];
        photos[cropModal.idx] = { ...photos[cropModal.idx], file: croppedFile };
        return { ...prev, photos };
      });
      closeCropModal();
    } catch (error) {
      console.error('Failed to crop image:', error);
      setPhotoError('We could not crop this image. Please try again or upload a different photo.');
    } finally {
      URL.revokeObjectURL(imageSrc);
    }
  };

  // Advanced image editor functions
  const openImageEditor = (fileOrUrl, idx) => {
    setImageEditor({ open: true, file: fileOrUrl, idx });
  };

  const closeImageEditor = () => {
    setImageEditor({ open: false, file: null, idx: null });
  };

  const handleImageEditorSave = editedFile => {
    if (imageEditor.idx !== null) {
      setForm(prev => {
        const photos = [...prev.photos];
        // Update the photo at the specified index with the edited file
        // Clear the url property since we now have a new file
        photos[imageEditor.idx] = {
          ...photos[imageEditor.idx],
          file: editedFile,
          url: null, // Clear the old URL since we have a new file
        };
        return { ...prev, photos };
      });
    }
    closeImageEditor();
  };

  // Image preview modal
  const openPreviewModal = (url, caption, photos = [], currentIndex = 0) => {
    setPreviewModal({
      open: true,
      url,
      caption,
      photos: photos.length > 0 ? photos : [{ url, caption }],
      currentIndex,
    });
  };

  const closePreviewModal = () =>
    setPreviewModal({
      open: false,
      url: '',
      caption: '',
      photos: [],
      currentIndex: 0,
    });

  const nextPhoto = useCallback(() => {
    if (previewModal.photos.length > 1) {
      const nextIndex = (previewModal.currentIndex + 1) % previewModal.photos.length;
      setPreviewModal(prev => ({
        ...prev,
        currentIndex: nextIndex,
        url: prev.photos[nextIndex].url,
        caption: prev.photos[nextIndex].caption,
      }));
    }
  }, [previewModal.photos.length, previewModal.currentIndex]);

  const prevPhoto = useCallback(() => {
    if (previewModal.photos.length > 1) {
      const prevIndex =
        previewModal.currentIndex === 0
          ? previewModal.photos.length - 1
          : previewModal.currentIndex - 1;
      setPreviewModal(prev => ({
        ...prev,
        currentIndex: prevIndex,
        url: prev.photos[prevIndex].url,
        caption: prev.photos[prevIndex].caption,
      }));
    }
  }, [previewModal.photos.length, previewModal.currentIndex]);

  // Keyboard navigation for gallery
  useEffect(() => {
    const handleKeyDown = e => {
      if (!previewModal.open) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          prevPhoto();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nextPhoto();
          break;
        case 'Escape':
          e.preventDefault();
          closePreviewModal();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    previewModal.open,
    previewModal.photos.length,
    previewModal.currentIndex,
    nextPhoto,
    prevPhoto,
  ]);

  // Render cropping modal
  const renderCropModal = () => {
    if (!cropModal.open || !cropModal.file) return null;
    const imageUrl = URL.createObjectURL(cropModal.file);
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.7)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            background: '#fff',
            padding: 20,
            borderRadius: 8,
            position: 'relative',
            minWidth: 350,
          }}
        >
          <h3>Crop Image</h3>
          <div style={{ width: 300, height: 300, position: 'relative' }}>
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              rotation={rotation}
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={onCropComplete}
              cropShape="rect"
              showGrid={true}
            />
          </div>
          <div style={{ margin: '10px 0' }}>
            <label>Aspect Ratio: </label>
            <select value={aspect} onChange={onAspectChange}>
              <option value={1}>1:1 (Square)</option>
              <option value={4 / 3}>4:3</option>
              <option value={16 / 9}>16:9</option>
              <option value={3 / 4}>3:4</option>
              <option value={9 / 16}>9:16</option>
              <option value={0}>Free</option>
            </select>
            <label style={{ marginLeft: 16 }}>Zoom: </label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={e => setZoom(Number(e.target.value))}
              style={{ width: 100 }}
            />
            <span style={{ marginLeft: 16 }}>
              <button
                type="button"
                onClick={() => onRotationChange(-90)}
                style={{ marginRight: 4 }}
              >
                ⟲ Rotate Left
              </button>
              <button type="button" onClick={() => onRotationChange(90)}>
                ⟳ Rotate Right
              </button>
            </span>
          </div>
          <div style={{ marginTop: 10, textAlign: 'right' }}>
            <button onClick={closeCropModal} style={{ marginRight: 10 }}>
              Cancel
            </button>
            <button onClick={handleCrop}>Crop & Save</button>
          </div>
        </div>
      </div>
    );
  };

  // Render preview modal
  const renderPreviewModal = () => {
    if (!previewModal.open) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-90 z-[60] flex items-center justify-center p-4">
        <div className="relative max-w-6xl max-h-full w-full h-full flex flex-col">
          {/* Close button */}
          <button
            onClick={closePreviewModal}
            className="absolute top-4 right-4 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-colors z-10"
            aria-label="Close gallery"
          >
            ✕
          </button>

          {/* Navigation arrows */}
          {previewModal.photos.length > 1 && (
            <>
              <button
                onClick={prevPhoto}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-75 transition-colors z-10"
                aria-label="Previous photo"
              >
                ‹
              </button>
              <button
                onClick={nextPhoto}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-75 transition-colors z-10"
                aria-label="Next photo"
              >
                ›
              </button>
            </>
          )}

          {/* Photo counter */}
          {previewModal.photos.length > 1 && (
            <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm z-10">
              {previewModal.currentIndex + 1} / {previewModal.photos.length}
            </div>
          )}

          {/* Main image */}
          <div className="flex-1 flex items-center justify-center">
            <img
              src={previewModal.url}
              alt={`${previewModal.currentIndex + 1}${previewModal.caption ? `: ${previewModal.caption}` : ''}`}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              style={{ maxHeight: 'calc(100vh - 200px)' }}
            />
          </div>

          {/* Caption */}
          {previewModal.caption && (
            <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white p-4 rounded-lg">
              <p className="text-sm">{previewModal.caption}</p>
            </div>
          )}

          {/* Thumbnail navigation */}
          {previewModal.photos.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 bg-black bg-opacity-50 p-2 rounded-lg">
              {previewModal.photos.map((photo, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setPreviewModal(prev => ({
                      ...prev,
                      currentIndex: index,
                      url: photo.url,
                      caption: photo.caption,
                    }));
                  }}
                  className={`w-12 h-12 rounded border-2 transition-all ${
                    index === previewModal.currentIndex
                      ? 'border-white opacity-100'
                      : 'border-gray-400 opacity-60 hover:opacity-80'
                  }`}
                >
                  <img
                    src={photo.url}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover rounded"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render image editor modal
  const renderImageEditor = () => {
    if (!imageEditor.open || !imageEditor.file) return null;

    // Create a URL for the image (either from file or URL string)
    const imageUrl =
      imageEditor.file instanceof File ? URL.createObjectURL(imageEditor.file) : imageEditor.file;

    return (
      <ImageEditor image={imageUrl} onSave={handleImageEditorSave} onCancel={closeImageEditor} />
    );
  };

  // Render map popup modal - REMOVED: Now using MapPicker component instead

  // On mount, check for logged-in user and load data
  useEffect(() => {
    const initializeApp = async () => {
      try {
        console.log('Initializing app...');
        // Check if we have a token
        const token = localStorage.getItem('swarm_token');
        console.log('Token found:', !!token);
        let loggedInUser = null;
        // Load reports for all users (public data)
        console.log('Loading reports for all users...');
        let loadedReports = [];
        try {
          const reportsResponse = await api.getReports();
          console.log('Reports loaded:', reportsResponse.reports?.length || 0, 'reports');
          loadedReports = reportsResponse.reports || [];
          setReports(loadedReports);
        } catch (error) {
          console.error('Failed to load reports:', error);
        }

        if (token) {
          console.log('Verifying token...');
          // Verify token and get user profile
          const response = await api.verifyToken();
          console.log('Token verified, user:', response.user);
          setUser(response.user);
          loggedInUser = response.user;

          // Note: Admin auto-redirect only happens on login, not on token verification
          console.log('🔍 Token verification - no admin redirect (only on login)');

          // Load pending reports for authenticated users (admins see all, normal users see their own)
          console.log('Loading pending reports...');
          try {
            const pendingResponse = await api.getPendingReports();
            console.log('Pending reports loaded:', pendingResponse.reports?.length || 0, 'reports');
            setPendingReports(pendingResponse.reports || []);
          } catch (error) {
            console.error('Failed to load pending reports:', error);
          }
        } else {
          console.log('No token found, user not authenticated');
        }
        // Set map center and zoom based on available reports
        console.log('🔍 Map centering: Loaded', loadedReports.length, 'reports');
        // Always use default center regardless of incident data
        console.log('🔍 Map centering: Using default center regardless of incident data');
        setMapCenter(DEFAULT_CENTER);
        setMapZoom(16);

        // Only fetch active area config if user is super admin
        if (loggedInUser && loggedInUser.isSuperAdmin) {
          console.log('Loading active area config (super admin)...');
          await fetchActiveArea();
        } else {
          // Set activeArea to null for non-super-admin users
          setActiveArea(null);
        }
      } catch (error) {
        console.error('Failed to initialize app:', error);
        // Clear invalid token
        api.setToken(null);
        setUser(null);
        setReports([]);
      } finally {
        setIsInitializing(false);
      }
    };
    initializeApp();
  }, [fetchActiveArea]);

  // Prefill reporter details if logged in
  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        reporterName: user.name,
        reporterEmail: user.email,
        reporterNumber: user.number || '',
      }));
    }
  }, [user]);

  // Auth form handlers
  function handleAuthChange(e) {
    const { name, value } = e.target;
    setAuthForm(prev => ({ ...prev, [name]: value, error: '' }));
  }
  async function handleRegister(e) {
    e.preventDefault();
    if (!authForm.name || !authForm.email || !authForm.password) {
      setAuthForm(prev => ({ ...prev, error: 'All fields except number are required.' }));
      return;
    }

    try {
      const response = await api.register({
        name: authForm.name,
        email: authForm.email,
        number: authForm.number,
        password: authForm.password,
      });

      api.setToken(response.token);
      setUser(response.user);
      setAuthForm({ name: '', email: '', number: '', password: '', error: '' });
      closeAllModals(); // Close auth modal after successful registration
    } catch (error) {
      setAuthForm(prev => ({ ...prev, error: error.message }));
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    console.log('🔐 handleLogin called - checking for 2FA requirement');
    console.log('🔐 Form data:', {
      email: authForm.email,
      password: authForm.password ? '***' : 'empty',
    });

    try {
      const response = await api.login({
        email: authForm.email,
        password: authForm.password,
      });

      // Check if 2FA is required
      if (response.requires2FA) {
        console.log('🔐 2FA required for login');
        setTwoFactorAuth({
          isRequired: true,
          tempToken: response.tempToken,
          token: '',
          error: '',
        });
        return; // Don't proceed with normal login flow
      }

      // Normal login flow (no 2FA)
      api.setToken(response.token);
      setUser(response.user);
      setAuthForm({ name: '', email: '', number: '', password: '', error: '', success: '' });
      closeAllModals(); // Close auth modal after successful login

      // Auto-redirect admin users to admin panel if on root page
      console.log('🔍 Login admin redirect check:', {
        isAdmin: response.user && response.user.isAdmin,
        adminRedirected,
        pathname: window.location.pathname,
        search: window.location.search,
        fullUrl: window.location.href,
      });
      if (
        response.user &&
        response.user.isAdmin &&
        !adminRedirected &&
        window.location.pathname === '/' &&
        window.location.search === ''
      ) {
        console.log('🔑 Admin user logged in, redirecting to admin panel...');
        setShowAdminPanel(true);
        setCurrentPage('admin');
        setAdminRedirected(true);
        sessionStorage.setItem('adminRedirected', 'true');
        // Set URL to admin panel
        window.history.pushState({}, '', '/?tab=reports');
        // Force a re-render by updating search params
        setSearchParams(new URLSearchParams('?tab=reports'));
      }

      // Fetch reports and pending reports after login
      const reportsResponse = await api.getReports();
      console.log('Loaded reports:', reportsResponse.reports?.length || 0);
      setReports(reportsResponse.reports || []);

      // Center map on incidents if available
      const loadedReports = reportsResponse.reports || [];
      if (loadedReports.length > 0) {
        const validReports = loadedReports.filter(report => report.lat && report.lng);
        if (validReports.length > 0) {
          const totalLat = validReports.reduce((sum, report) => sum + report.lat, 0);
          const totalLng = validReports.reduce((sum, report) => sum + report.lng, 0);
          const centerLat = totalLat / validReports.length;
          const centerLng = totalLng / validReports.length;

          setMapCenter({ lat: centerLat, lng: centerLng });
          setMapZoom(14);
        }
      }

      console.log('🔍 About to call getPendingReports, token available:', !!api.getToken());
      const pendingResponse = await api.getPendingReports();
      setPendingReports(pendingResponse.reports || []);

      // Fetch active area config after login
      await fetchActiveArea();
    } catch (error) {
      setAuthForm(prev => ({ ...prev, error: error.message }));
      handleRateLimitError(error);
    }
  }

  async function handle2FAVerification(e) {
    e.preventDefault();
    console.log('🔐 handle2FAVerification called');
    console.log('🔐 2FA token:', twoFactorAuth.token);
    console.log('🔐 tempToken:', twoFactorAuth.tempToken ? 'Present' : 'Missing');
    console.log('🔐 Event object:', e);

    if (!twoFactorAuth.token) {
      setTwoFactorAuth(prev => ({ ...prev, error: 'Please enter your 6-digit code.' }));
      return;
    }

    try {
      console.log('🔐 About to call verify2FA API...');
      const response = await api.verify2FA(twoFactorAuth.token, twoFactorAuth.tempToken);
      console.log('🔐 verify2FA API response:', response);

      // Successful 2FA verification
      api.setToken(response.token);
      console.log('🔐 Token set after 2FA verification:', response.token ? 'Present' : 'Missing');
      setUser(response.user);
      setTwoFactorAuth({ isRequired: false, tempToken: '', token: '', error: '' });
      setAuthForm({ name: '', email: '', number: '', password: '', error: '', success: '' });
      closeAllModals();

      // Auto-redirect admin users to admin panel if on root page
      if (
        response.user &&
        response.user.isAdmin &&
        !adminRedirected &&
        window.location.pathname === '/' &&
        window.location.search === ''
      ) {
        console.log('🔑 Admin user logged in with 2FA, redirecting to admin panel...');
        setShowAdminPanel(true);
        setCurrentPage('admin');
        setAdminRedirected(true);
        sessionStorage.setItem('adminRedirected', 'true');
        window.history.pushState({}, '', '/?tab=reports');
        setSearchParams(new URLSearchParams('?tab=reports'));
      }

      // Fetch reports and pending reports after login
      try {
        const reportsResponse = await api.getReports();
        setReports(reportsResponse.reports || []);

        const pendingResponse = await api.getPendingReports();
        setPendingReports(pendingResponse.reports || []);

        await fetchActiveArea();
      } catch (fetchError) {
        console.error('Error fetching data after 2FA login:', fetchError);
        // Don't show this error to the user as login was successful
      }
    } catch (error) {
      console.error('🔐 2FA verification error:', error);
      setTwoFactorAuth(prev => ({ ...prev, error: error.message }));
    }
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    if (!authForm.email) {
      setAuthForm(prev => ({ ...prev, error: 'Email is required.' }));
      return;
    }

    try {
      await api.forgotPassword(authForm.email);
      setAuthForm(prev => ({
        ...prev,
        error: '',
        success: 'Password reset instructions have been sent to your email address.',
      }));
      announceToScreenReader('Password reset email sent');
    } catch (error) {
      console.error('Forgot password failed:', error);
      setAuthForm(prev => ({ ...prev, error: error.message, success: '' }));
    }
  }
  function handleLogout() {
    api.setToken(null);
    setUser(null);
    closeAllModals();
    // Clear admin redirect flag
    sessionStorage.removeItem('adminRedirected');
    // Navigate to main page without URL parameters
    window.location.replace('/');
  }

  // Auth UI
  function renderAuth() {
    return (
      <div className="w-full">
        {/* Dynamic Logo and Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl shadow-lg mb-4">
            {brandingConfig?.logo_url ? (
              <img src={brandingConfig.logo_url} alt="Logo" className="w-12 h-12 rounded" />
            ) : (
              <img src="/SWARM_logo_no_tagline.svg" alt="SWARM Logo" className="w-12 h-12" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-primary-800 mb-2">
            {brandingConfig?.site_name || 'SWARM'}
          </h1>
          <p className="text-lg text-primary-600 font-medium mb-1">
            {brandingConfig?.strapline || 'Incident Reporter'}
          </p>

          <p className="text-xs text-gray-500">
            <span className="font-semibold text-primary-600">S</span>afer{' '}
            <span className="font-semibold text-primary-600">W</span>alking{' '}
            <span className="font-semibold text-primary-600">A</span>nd{' '}
            <span className="font-semibold text-primary-600">R</span>iding for{' '}
            <span className="font-semibold text-primary-600">M</span>urray Rd
          </p>
        </div>

        {/* Auth Form */}
        <div className="bg-white rounded-2xl shadow-soft p-8 border border-gray-100">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {authMode === 'login'
                ? 'Welcome Back'
                : authMode === 'register'
                  ? 'Create Account'
                  : 'Reset Password'}
            </h2>
            <p className="text-gray-600">
              {authMode === 'login'
                ? 'Sign in to your SWARM account'
                : authMode === 'register'
                  ? 'Join the SWARM community'
                  : 'Enter your email to reset your password'}
            </p>
          </div>

          <form
            onSubmit={
              authMode === 'login'
                ? handleLogin
                : authMode === 'register'
                  ? handleRegister
                  : handleForgotPassword
            }
            className="space-y-5"
          >
            {authMode === 'register' && (
              <div className="form-group relative">
                <label htmlFor="auth_name" className="form-label">
                  Full Name
                </label>
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <UserIcon className="w-5 h-5" />
                </span>
                <input
                  type="text"
                  id="auth_name"
                  name="name"
                  value={authForm.name}
                  onChange={handleAuthChange}
                  className="form-input pl-12"
                  placeholder="Enter your full name"
                />
              </div>
            )}
            <div className="form-group relative">
              <label htmlFor="auth_email" className="form-label">
                Email Address
              </label>
              <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <Mail className="w-5 h-5" />
              </span>
              <input
                type="email"
                id="auth_email"
                name="email"
                value={authForm.email}
                onChange={handleAuthChange}
                className="form-input pl-12"
                placeholder="Enter your email address"
              />
            </div>
            {authMode === 'register' && (
              <div className="form-group relative">
                <label htmlFor="auth_number" className="form-label">
                  Contact Number (Optional)
                </label>
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <UserIcon className="w-5 h-5" />
                </span>
                <input
                  type="text"
                  id="auth_number"
                  name="number"
                  value={authForm.number}
                  onChange={handleAuthChange}
                  className="form-input pl-12"
                  placeholder="Enter your phone number"
                />
              </div>
            )}
            {(authMode === 'login' || authMode === 'register') && (
              <div className="form-group relative">
                <label htmlFor="auth_password" className="form-label">
                  Password
                </label>
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <Lock className="w-5 h-5" />
                </span>
                <input
                  type="password"
                  id="auth_password"
                  name="password"
                  value={authForm.password}
                  onChange={handleAuthChange}
                  className="form-input pl-12"
                  placeholder="Enter your password"
                />
              </div>
            )}

            {/* Error and Success Messages */}
            {authForm.error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {authForm.error}
              </div>
            )}
            {authForm.success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                {authForm.success}
              </div>
            )}

            {/* 2FA Verification */}
            {twoFactorAuth.isRequired && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
                    <svg
                      className="w-6 h-6 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Two-Factor Authentication
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Please enter the 6-digit code from your authenticator app
                  </p>
                </div>

                <form
                  onSubmit={e => {
                    console.log('🔐 Form onSubmit triggered');
                    handle2FAVerification(e);
                  }}
                  className="space-y-4"
                >
                  <div className="form-group">
                    <label htmlFor="twofa_token" className="form-label">
                      Authentication Code
                    </label>
                    <input
                      type="text"
                      id="twofa_token"
                      value={twoFactorAuth.token}
                      onChange={e => setTwoFactorAuth(prev => ({ ...prev, token: e.target.value }))}
                      className="form-input text-center text-lg tracking-widest"
                      placeholder="000000"
                      maxLength="6"
                      pattern="[0-9]{6}"
                    />
                  </div>

                  {twoFactorAuth.error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                      {twoFactorAuth.error}
                    </div>
                  )}

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() =>
                        setTwoFactorAuth({ isRequired: false, tempToken: '', token: '', error: '' })
                      }
                      className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      onClick={e => {
                        console.log('🔐 Verify button clicked');
                        e.preventDefault();
                        handle2FAVerification(e);
                      }}
                      className="flex-1 bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 transition-colors"
                    >
                      Verify
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Submit Button */}
            {!twoFactorAuth.isRequired && (
              <button
                type="submit"
                className="w-full btn btn-primary py-3 text-base font-medium"
                disabled={
                  authMode === 'login'
                    ? !authForm.email || !authForm.password
                    : authMode === 'register'
                      ? !authForm.name || !authForm.email || !authForm.password
                      : !authForm.email
                }
              >
                {authMode === 'login'
                  ? 'Sign In'
                  : authMode === 'register'
                    ? 'Create Account'
                    : 'Send Reset Email'}
              </button>
            )}

            {/* Continue as Guest Button */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  closeAllModals();
                  setShowReportForm(true);
                }}
                className="text-primary-600 hover:text-primary-700 font-medium text-sm underline"
              >
                Continue as Guest - Submit Report Without Account
              </button>
            </div>
          </form>

          {/* Auth Mode Switcher */}
          <div className="mt-6 text-center">
            {authMode === 'login' ? (
              <p className="text-gray-600">
                Don't have an account?{' '}
                <button
                  onClick={() => {
                    setAuthMode('register');
                    setShowAuthModal(true);
                  }}
                  className="text-primary-600 hover:text-primary-700 font-medium underline"
                >
                  Sign up here
                </button>
              </p>
            ) : authMode === 'register' ? (
              <p className="text-gray-600">
                Already have an account?{' '}
                <button
                  onClick={() => {
                    setAuthMode('login');
                    setShowAuthModal(true);
                  }}
                  className="text-primary-600 hover:text-primary-700 font-medium underline"
                >
                  Sign in here
                </button>
              </p>
            ) : null}

            {authMode !== 'forgot' && (
              <button
                onClick={() => {
                  setAuthMode('forgot');
                  setShowAuthModal(true);
                }}
                className="text-primary-600 hover:text-primary-700 font-medium text-sm underline mt-2 block"
              >
                Forgot your password?
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Export reports as CSV
  // Report approval functions
  const handleApproveReport = async reportId => {
    try {
      await api.approveReport(reportId);
      // Refresh both reports and pending reports after approval
      const reportsResponse = await api.getReports();
      setReports(reportsResponse.reports || []);
      const pendingResponse = await api.getPendingReports();
      setPendingReports(pendingResponse.reports || []);
      console.log('Report approved successfully');
    } catch (error) {
      console.error('Failed to approve report:', error);
    }
  };

  const handleRejectReport = async reportId => {
    try {
      await api.rejectReport(reportId);
      // Refresh both reports and pending reports after rejection
      const reportsResponse = await api.getReports();
      setReports(reportsResponse.reports || []);
      const pendingResponse = await api.getPendingReports();
      setPendingReports(pendingResponse.reports || []);
      console.log('Report rejected successfully');
    } catch (error) {
      console.error('Failed to reject report:', error);
    }
  };

  const handleDeleteReport = async reportId => {
    try {
      await api.deleteReport(reportId);
      // Refresh reports after deletion
      const reportsResponse = await api.getReports();
      setReports(reportsResponse.reports || []);
      console.log('Report deleted successfully');
    } catch (error) {
      console.error('Failed to delete report:', error);
    }
  };

  const handleRevokeApproval = async reportId => {
    try {
      await api.revokeApproval(reportId);
      // Refresh reports after revoking approval
      const reportsResponse = await api.getReports();
      setReports(reportsResponse.reports || []);
      console.log('Report approval revoked successfully');
    } catch (error) {
      console.error('Failed to revoke report approval:', error);
    }
  };

  const handleRestoreReport = async reportId => {
    try {
      await api.restoreReport(reportId);
      // Refresh both reports and pending reports after restoring
      const reportsResponse = await api.getReports();
      setReports(reportsResponse.reports || []);
      const pendingResponse = await api.getPendingReports();
      setPendingReports(pendingResponse.reports || []);
      console.log('Report restored to pending successfully');
    } catch (error) {
      console.error('Failed to restore report:', error);
    }
  };

  const handleDeleteUser = async email => {
    // Show confirmation dialog before deleting user
    showConfirmationModal(
      'Delete User',
      `Are you sure you want to delete the user "${email}"? This action will permanently delete:
      
• User's account and profile
• All photos uploaded by this user
• All extra notes created by this user
• User's alert preferences

Reports submitted by this user will be kept but anonymized (reporter name will be set to "Anonymous User" and phone number removed). The original email address will be preserved for historical/audit purposes.

This action cannot be undone.`,
      async () => {
        try {
          await api.deleteUser(email);
          // Refresh user list after deletion
          const usersResponse = await api.getUsers();
          setUserList(usersResponse.users || []);
          console.log('User deleted successfully');
        } catch (error) {
          console.error('Failed to delete user:', error);
          // Show error message to user
          alert(`Failed to delete user: ${error.message}`);
        }
      },
      { confirmText: 'Delete User', cancelText: 'Cancel', danger: true },
    );
  };

  const handleToggleAdmin = async email => {
    try {
      await api.toggleAdmin(email);
      // Refresh user list after toggling admin status
      const usersResponse = await api.getUsers();
      setUserList(usersResponse.users || []);
      console.log('User admin status updated successfully');
    } catch (error) {
      console.error('Failed to toggle user admin status:', error);
    }
  };

  const handleToggle2FA = async email => {
    try {
      await api.toggle2FA(email);
      // Refresh user list after toggling 2FA status
      const usersResponse = await api.getUsers();
      setUserList(usersResponse.users || []);
      console.log('User 2FA status updated successfully');
    } catch (error) {
      console.error('Failed to toggle user 2FA status:', error);
    }
  };

  const exportReportsToCSV = (includeImages = false) => {
    api.exportReports(includeImages);
  };

  const importReportsFromSpreadsheet = async (file, importAsApproved = false) => {
    try {
      // Parse CSV file using Papa Parse
      const Papa = (await import('papaparse')).default;

      return new Promise((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: async results => {
            try {
              // Transform CSV data to match backend expectations
              const reports = results.data.map(row => ({
                reporterName: row['reporter name'] || row['Reporter Name'] || '',
                reporterEmail: row['reporter email'] || row['Reporter Email'] || '',
                reporterNumber: row['contact number'] || row['Contact Number'] || '',
                location: row['location'] || row['Location'] || '',
                coordinates: row['coordinates'] || row['Coordinates'] || '',
                incidentType: row['incident type'] || row['Incident Type'] || '',
                description: row['description'] || row['Description'] || '',
                estimatedIncidentTime: row['estimated time'] || row['Estimated Time'] || '',
                witnessedIncident: row['witnessed'] || row['Witnessed'] || '',
                evidenceDescription:
                  row['evidence description'] || row['Evidence Description'] || '',
                emergencyServicesAttended:
                  row['emergency services attended'] || row['Emergency Services Attended'] || '',
                policeAttended:
                  row['police attended'] === '1' || row['Police Attended'] === '1' || false,
                ambulanceAttended:
                  row['ambulance attended'] === '1' || row['Ambulance Attended'] === '1' || false,
                fireBrigadeAttended:
                  row['fire brigade attended'] === '1' ||
                  row['Fire Brigade Attended'] === '1' ||
                  false,
                policeReportTime: row['police report time'] || row['Police Report Time'] || '',
                policeOfficerName: row['police officer name'] || row['Police Officer Name'] || '',
                policeReportNumber:
                  row['police report number'] || row['Police Report Number'] || '',
                injuries: row['injuries'] || row['Injuries'] || '',
                injuryCount: parseInt(row['injury count'] || row['Injury Count'] || '0', 10) || 0,
                vehiclesInvolved: (() => {
                  const raw =
                    row['vehicles involved'] ||
                    row['Vehicles Involved'] ||
                    row['vehicle count'] ||
                    row['Vehicle Count'];
                  if (raw === undefined || raw === null || raw === '') return null;
                  const parsed = parseInt(raw, 10);
                  return Number.isNaN(parsed) ? null : parsed;
                })(),
                isApproved: row['is approved'] === '1' || row['is approved'] === 'true' || false,
                approvedBy: row['approved by'] || row['Approved By'] || '',
                approvedAt: row['approved at'] || row['Approved At'] || null,
              }));

              // Filter out empty rows
              const validReports = reports.filter(
                report => report.reporterName && report.location && report.description,
              );

              if (validReports.length === 0) {
                reject(new Error('No valid reports found in CSV file'));
                return;
              }

              // Send to backend
              const response = await api.importReports(validReports, importAsApproved);

              // Refresh reports list to show newly imported data
              try {
                const reportsResponse = await api.getReports();
                setReports(reportsResponse.reports || []);
                console.log('✅ Reports refreshed after import');
              } catch (refreshError) {
                console.error('Failed to refresh reports after import:', refreshError);
              }

              resolve(response);
            } catch (error) {
              reject(error);
            }
          },
          error: error => {
            reject(new Error(`CSV parsing error: ${error.message}`));
          },
        });
      });
    } catch (error) {
      throw new Error(`Import failed: ${error.message}`);
    }
  };

  useEffect(() => {
    if (showAdminPanel && user && (user.isAdmin || user.isSuperAdmin)) {
      api
        .getUsers()
        .then(res => {
          setUserList(res.users || []);
        })
        .catch(err => {
          setUserList([]);
          console.error('Failed to load users:', err);
        });

      // Load alert data for admin users
      loadAlertData();
    }
  }, [showAdminPanel, user]);

  const vehiclesInfo = getVehiclesInvolvedDisplay(selectedReportForDetails);
  const isAdminReportsTable = Boolean(user?.isAdmin || user?.isSuperAdmin);
  const speedsStatisticsPage = showStatistics && searchParams.get('stats') === 'speeds';
  const statisticsPageTitle = speedsStatisticsPage ? 'Murray Road speeds' : copy.statisticsTitle;

  const saferMurrayRoadBackLink = (extraClassName = '') => (
    <a
      href="https://safermurrayroad.com"
      target="_blank"
      rel="noopener noreferrer"
      className={`text-sm text-blue-600 hover:text-blue-800 underline whitespace-nowrap swarm-header-back-link ${extraClassName}`.trim()}
      data-testid="back-to-safermurrayroad-link"
    >
      ← Back to safermurrayroad.com
    </a>
  );

  return (
    <div
      className={
        speedsStatisticsPage
          ? 'flex min-h-screen flex-col swarm-app-shell'
          : 'flex h-screen flex-col swarm-app-shell'
      }
      data-testid="app-container"
    >
      {/* Header - Only show when user is logged in */}
      {user && (
        <header className="swarm-app-header bg-white shadow-md p-4" data-testid="header">
          <div className="swarm-app-header__body">
            {/* Desktop Layout */}
            <div className="hidden md:flex justify-between items-center">
              <div className="flex items-center gap-3 min-w-0">
                {/* Logo */}
                {brandingConfig?.logo_url ? (
                  <img
                    src={brandingConfig.logo_url}
                    alt="Logo"
                    className="w-10 h-10 rounded mr-2"
                    data-testid="logo"
                  />
                ) : (
                  <img
                    src="/SWARM_logo_no_tagline.svg"
                    alt="SWARM Logo"
                    className="w-10 h-10 mr-2"
                    data-testid="logo"
                  />
                )}
                {/* Site Name and Strapline */}
                <div className="flex flex-col min-w-0">
                  <span
                    className="text-xl font-bold text-primary-800 truncate"
                    data-testid="site-name"
                  >
                    {brandingConfig?.site_name || 'SWARM'}
                  </span>
                  {brandingConfig?.strapline && (
                    <span className="text-xs text-gray-500 truncate" data-testid="site-strapline">
                      {brandingConfig.strapline}
                    </span>
                  )}
                </div>
                {/* Page/Screen Name */}
                <span
                  className="ml-4 text-lg font-semibold text-primary-600 truncate"
                  data-testid="page-name"
                >
                  {showStatistics
                    ? statisticsPageTitle
                    : showAdminPanel
                      ? 'Admin Panel'
                      : 'Incident Reports'}
                </span>
              </div>
              <div className="flex items-center flex-wrap justify-end gap-x-4 gap-y-2">
                {saferMurrayRoadBackLink('shrink-0')}
                {/* Submit Incident Report Button - PRIMARY ACTION for all logged-in users */}
                {!showStatistics && user && (
                  <button
                    onClick={() => {
                      closeAllModals();
                      setShowReportForm(true);
                    }}
                    className="btn btn-primary text-sm font-semibold px-4 py-2"
                    data-testid="submit-report-button"
                  >
                    {copy.submitReport}
                  </button>
                )}

                {/* Back Button for Statistics Screen */}
                {showStatistics && (
                  <button
                    onClick={() => {
                      closeAllModals();
                      // Clear URL parameters to go back to main
                      const newSearchParams = new URLSearchParams();
                      setSearchParams(newSearchParams);
                    }}
                    className="btn btn-secondary text-sm"
                    data-testid="back-button"
                  >
                    ← Back
                  </button>
                )}
                {/* Report Statistics Button - All users */}
                {!showStatistics && (
                  <button
                    onClick={() => {
                      closeAllModals();
                      // Set URL to statistics view
                      const newSearchParams = new URLSearchParams();
                      newSearchParams.set('view', 'statistics');
                      setSearchParams(newSearchParams);
                    }}
                    className="btn btn-secondary text-sm"
                    data-testid="report-statistics-button"
                  >
                    {copy.reportStatistics}
                  </button>
                )}
                {/* Back to Main Button - Show when admin panel is active */}
                {showAdminPanel && (
                  <button
                    onClick={() => {
                      closeAllModals();
                      // Clear URL parameters to go back to main
                      const newSearchParams = new URLSearchParams();
                      setSearchParams(newSearchParams);
                    }}
                    className="btn btn-secondary text-sm"
                    data-testid="back-to-main-button"
                  >
                    ← Back to Main
                  </button>
                )}
                {/* Admin Panel Button - Show for admin users only */}
                {!showAdminPanel &&
                  !showStatistics &&
                  user &&
                  (user.isAdmin || user.isSuperAdmin) && (
                    <button
                      onClick={() => {
                        closeAllModals();
                        // Set URL to admin panel with default tab
                        const newSearchParams = new URLSearchParams();
                        newSearchParams.set('tab', 'reports');
                        setSearchParams(newSearchParams);
                      }}
                      className="btn btn-secondary text-sm"
                      data-testid="admin-panel-button"
                    >
                      Admin Panel
                    </button>
                  )}
                <div className="flex items-center">
                  {user ? (
                    <>
                      <span
                        className="text-sm font-medium text-gray-600 mr-2"
                        data-testid="user-name"
                      >
                        {user.name}
                      </span>
                      <button
                        onClick={() => setShowUserSettings(true)}
                        className="btn btn-secondary mr-2"
                        data-testid="settings-button"
                        title="Account Settings"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleLogout}
                        className="btn btn-secondary"
                        data-testid="logout-button"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          closeAllModals();
                          setAuthMode('login');
                          setShowAuthModal(true);
                        }}
                        className="btn btn-secondary text-sm mr-2"
                        data-testid="login-button"
                      >
                        Login
                      </button>
                      <button
                        onClick={() => {
                          closeAllModals();
                          setAuthMode('register');
                          setShowAuthModal(true);
                        }}
                        className="btn btn-primary text-sm"
                        data-testid="register-button"
                      >
                        Register
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile Layout */}
            <div className="md:hidden">
              {/* Top Row - Logo, Title, Menu Button */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {/* Logo */}
                  {brandingConfig?.logo_url ? (
                    <img
                      src={brandingConfig.logo_url}
                      alt="Logo"
                      className="w-8 h-8 rounded"
                      data-testid="logo-mobile"
                    />
                  ) : (
                    <img
                      src="/SWARM_logo_no_tagline.svg"
                      alt="SWARM Logo"
                      className="w-8 h-8"
                      data-testid="logo-mobile"
                    />
                  )}
                  {/* Site Name */}
                  <span
                    className="text-lg font-bold text-primary-800 truncate"
                    data-testid="site-name-mobile"
                  >
                    {brandingConfig?.site_name || 'SWARM'}
                  </span>
                </div>

                {/* Mobile Menu Button */}
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                  aria-label="Toggle mobile menu"
                  data-testid="mobile-menu-button"
                >
                  {showMobileMenu ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    </svg>
                  )}
                </button>
              </div>

              {/* Page Title */}
              <div className="mb-3 flex flex-col gap-1">
                <span
                  className="text-base font-semibold text-primary-600"
                  data-testid="page-name-mobile"
                >
                  {showStatistics
                    ? statisticsPageTitle
                    : showAdminPanel
                      ? 'Admin Panel'
                      : 'Incident Reports'}
                </span>
                {saferMurrayRoadBackLink('text-xs self-start')}
              </div>

              {/* Mobile Menu Dropdown */}
              {showMobileMenu && (
                <div className="bg-gray-50 rounded-lg p-3 space-y-2 border border-gray-200">
                  {/* Navigation Buttons */}
                  <div className="space-y-2">
                    {/* Submit Incident Report Button - PRIMARY ACTION for all logged-in users */}
                    {!showStatistics && user && (
                      <button
                        onClick={() => {
                          closeAllModals();
                          setShowMobileMenu(false);
                          setShowReportForm(true);
                        }}
                        className="w-full btn btn-primary text-sm font-semibold justify-center py-3"
                        data-testid="submit-report-button-mobile"
                      >
                        {copy.submitReport}
                      </button>
                    )}

                    {/* Back Button for Statistics Screen */}
                    {showStatistics && (
                      <button
                        onClick={() => {
                          closeAllModals();
                          setShowMobileMenu(false);
                          // Clear URL parameters to go back to main
                          const newSearchParams = new URLSearchParams();
                          setSearchParams(newSearchParams);
                        }}
                        className="w-full btn btn-secondary text-sm justify-center"
                        data-testid="back-button-mobile"
                      >
                        ← Back
                      </button>
                    )}

                    {/* Report Statistics Button - All users */}
                    {!showStatistics && (
                      <button
                        onClick={() => {
                          closeAllModals();
                          setShowMobileMenu(false);
                          // Set URL to statistics view
                          const newSearchParams = new URLSearchParams();
                          newSearchParams.set('view', 'statistics');
                          setSearchParams(newSearchParams);
                        }}
                        className="w-full btn btn-secondary text-sm justify-center"
                        data-testid="report-statistics-button-mobile"
                      >
                        {copy.reportStatistics}
                      </button>
                    )}

                    {/* Back to Main Button - Show when admin panel is active */}
                    {showAdminPanel && (
                      <button
                        onClick={() => {
                          closeAllModals();
                          setShowMobileMenu(false);
                          // Clear URL parameters to go back to main
                          const newSearchParams = new URLSearchParams();
                          setSearchParams(newSearchParams);
                        }}
                        className="w-full btn btn-secondary text-sm justify-center"
                        data-testid="back-to-main-button-mobile"
                      >
                        ← Back to Main
                      </button>
                    )}

                    {/* Admin Panel Button - Show for admin users only */}
                    {!showAdminPanel &&
                      !showStatistics &&
                      user &&
                      (user.isAdmin || user.isSuperAdmin) && (
                        <button
                          onClick={() => {
                            closeAllModals();
                            setShowMobileMenu(false);
                            // Set URL to admin panel with default tab
                            const newSearchParams = new URLSearchParams();
                            newSearchParams.set('tab', 'reports');
                            setSearchParams(newSearchParams);
                          }}
                          className="w-full btn btn-secondary text-sm justify-center"
                          data-testid="admin-panel-button-mobile"
                        >
                          Admin Panel
                        </button>
                      )}
                  </div>

                  {/* User Info and Logout */}
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="flex items-center justify-between">
                      <span
                        className="text-sm font-medium text-gray-600"
                        data-testid="user-name-mobile"
                      >
                        {user.name}
                      </span>
                      <button
                        onClick={() => {
                          setShowMobileMenu(false);
                          handleLogout();
                        }}
                        className="btn btn-secondary text-sm"
                        data-testid="logout-button-mobile"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {isCorridor && <div className="swarm-app-header__centre-line" aria-hidden="true" />}
        </header>
      )}

      {/* Main Content */}
      <main
        className={
          showAdminPanel && user
            ? 'flex-1 h-screen w-screen p-0 m-0'
            : speedsStatisticsPage
              ? 'w-full p-0'
              : 'flex-1 p-8'
        }
        data-testid="main-content"
      >
        {/* Anonymous Report Form - Show when user is not logged in and report form is active */}
        {!user && showReportForm && (
          <div className="w-full max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Submit Incident Report</h1>
                <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                  {saferMurrayRoadBackLink()}
                  <button onClick={closeAllModals} className="btn btn-secondary">
                    Back to Map
                  </button>
                  <button
                    onClick={() => {
                      closeAllModals();
                      setAuthMode('login');
                      setShowAuthModal(true);
                    }}
                    className="btn btn-primary"
                  >
                    Sign In / Register
                  </button>
                </div>
              </div>
              <p className="text-gray-600 mb-6">
                You can submit a report without creating an account. Your report will be reviewed by
                administrators.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <span className="text-blue-600 text-lg">💡</span>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800 mb-1">
                      Registration is Optional
                    </h3>
                    <p className="text-sm text-blue-700">
                      Creating an account only helps pre-fill your details when submitting reports.
                      <strong>
                        {' '}
                        None of your personal information will be visible to the public.
                      </strong>
                    </p>
                  </div>
                </div>
              </div>
              {/* Report form will be rendered here */}
            </div>
          </div>
        )}

        {/* Welcome Section for Anonymous Users - Show when user is not logged in and not showing report form */}
        {!user && !showReportForm && !showStatistics && (
          <PublicWelcomeHeader
            variant="welcome"
            brandingConfig={brandingConfig}
            backLink={saferMurrayRoadBackLink()}
            onSubmitReport={() => {
              closeAllModals();
              setShowReportForm(true);
            }}
            onStatistics={openStatisticsView}
            onAuth={() => {
              closeAllModals();
              setAuthMode('login');
              setShowAuthModal(true);
            }}
          />
        )}

        {/* Anonymous User Statistics Header */}
        {!user && showStatistics && !speedsStatisticsPage && (
          <PublicWelcomeHeader
            variant="statistics"
            pageTitle={statisticsPageTitle}
            brandingConfig={brandingConfig}
            backLink={saferMurrayRoadBackLink()}
            onSubmitReport={() => {
              closeAllModals();
              setShowReportForm(true);
            }}
            onStatistics={() => {}}
            onAuth={() => {
              closeAllModals();
              setAuthMode('login');
              setShowAuthModal(true);
            }}
            onBackToMain={() => {
              closeAllModals();
              const newSearchParams = new URLSearchParams();
              setSearchParams(newSearchParams);
            }}
          />
        )}

        {/* Report Statistics Screen */}
        {showStatistics && (
          <div className={speedsStatisticsPage ? 'w-full' : 'w-full h-full'}>
            <StatisticsView reports={reports} user={user} />
          </div>
        )}

        {/* Admin Panel - Show when admin panel is active */}
        {!showStatistics && showAdminPanel && user && (
          <AdminPanel
            user={user}
            userList={userList}
            setUserList={setUserList}
            editingUser={editingUser}
            userEditForm={userEditForm}
            setEditingUser={setEditingUser}
            setUserEditForm={setUserEditForm}
            handleEditUser={user => {
              setEditingUser(user);
              setUserEditForm({
                name: user.name || '',
                email: user.email || '',
                number: user.number || '',
                password: '',
                isAdmin: !!user.isAdmin,
                error: '',
                success: '',
              });
            }}
            handleDeleteUser={handleDeleteUser}
            handleToggleAdmin={handleToggleAdmin}
            handleToggle2FA={handleToggle2FA}
            exportReportsToCSV={exportReportsToCSV}
            importReportsFromSpreadsheet={importReportsFromSpreadsheet}
            saveAlertRecipients={api.saveAlertRecipients.bind(api)}
            saveUserAlertPreferences={api.saveUserAlertPreferences.bind(api)}
            toggleUserAlertPreference={api.toggleUserAlertPreference.bind(api)}
            getUsersForAlertType={api.getUsersForAlertType.bind(api)}
            showDemoAlert={showDemoAlert}
            alertRecipients={alertRecipients}
            userAlertPreferences={userAlertPreferences}
            setUserAlertPreferences={setUserAlertPreferences}
            setShowAlertModal={() => {}}
            setAlertContent={() => {}}
            setEmails={() => {}}
            setPhones={() => {}}
            emails={[]}
            phones={[]}
            setShowUserPanel={() => {}}
            setCurrentPage={() => {}}
            handleDeleteReport={handleDeleteReport}
            handleApproveReport={handleApproveReport}
            handleRejectReport={handleRejectReport}
            handleRevokeApproval={handleRevokeApproval}
            handleRestoreReport={handleRestoreReport}
            reports={reports}
            pendingReports={pendingReports}
            setPendingReports={setPendingReports}
            setIsAdminMode={() => {}}
            setShowReportForm={setShowReportForm}
            showAdminPanel={showAdminPanel}
            setShowAdminPanel={setShowAdminPanel}
            setReports={setReports}
            onActiveAreaChange={() => {}}
            activeArea={null}
            onBackToMain={() => {
              setShowAdminPanel(false);
              // Clear admin redirect flag when going back to main
              sessionStorage.removeItem('adminRedirected');
            }}
          />
        )}

        {/* Main App Content - Show when admin panel is not active (public for all users) */}
        {!showAdminPanel && !showStatistics && (
          <>
            <CorridorStatsStrip reports={reports} onOpenStatistics={openStatisticsView} />
            {/* Main Layout - Map and Table Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mb-6">
              {/* Map Section - Left Side */}
              <section
                className="swarm-map-section bg-white rounded-lg shadow-sm border border-gray-200 p-3 lg:p-4"
                data-testid="map-section"
              >
                <div className="flex flex-col gap-3 mb-4">
                  {/* Title */}
                  <h2
                    className="text-lg lg:text-xl font-semibold text-gray-800"
                    data-testid="map-title"
                  >
                    {copy.incidentMap}
                  </h2>
                </div>

                <div
                  className="relative h-[400px] lg:h-[500px] rounded-lg overflow-hidden"
                  ref={mapContainerRef}
                  data-testid="map-container"
                >
                  {!isLoaded ? (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
                      <div className="text-center">
                        <div
                          className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"
                          data-testid="loading-spinner"
                        ></div>
                        <p className="text-sm text-gray-600">Loading map...</p>
                      </div>
                    </div>
                  ) : (
                    <GoogleMap
                      key={`map-${JSON.stringify(defaultCenter)}-${defaultZoom}`}
                      mapContainerStyle={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '12px',
                        position: 'relative',
                        zIndex: 1,
                      }}
                      onLoad={map => {
                        mapRef.current = map;
                        // Create a lightweight OverlayView to access projection for pixel conversions
                        try {
                          const overlay = new window.google.maps.OverlayView();
                          overlay.onAdd = function onAdd() {};
                          overlay.draw = function draw() {};
                          overlay.onRemove = function onRemove() {};
                          overlay.setMap(map);
                          overlayRef.current = overlay;
                        } catch (e) {
                          overlayRef.current = null;
                        }
                      }}
                      onUnmount={() => {
                        if (overlayRef.current) {
                          try {
                            overlayRef.current.setMap(null);
                          } catch (e) {}
                        }
                        overlayRef.current = null;
                        mapRef.current = null;
                      }}
                      center={
                        activeArea && activeArea.center_lat && activeArea.center_lng
                          ? { lat: activeArea.center_lat, lng: activeArea.center_lng }
                          : mapCenter
                      }
                      zoom={activeArea && activeArea.zoom_level ? activeArea.zoom_level : mapZoom}
                      onClick={handleMapClick}
                      options={
                        getMurrayRoadMapConfig({
                          center: mapCenter,
                          zoom: mapZoom,
                          restriction: mapRestrictions?.enabled
                            ? {
                                latLngBounds: mapRestrictions.bounds,
                                strictBounds: mapRestrictions.strictBounds,
                              }
                            : undefined,
                          mapOptions: {
                            disableDefaultUI: true,
                            zoomControl: true,
                          },
                          mapStyles: [],
                        }).options
                      }
                    >
                      {/* Report markers - show individual markers when not in heatmap mode */}
                      {!showHeatmap &&
                        reportMarkers.map(marker => (
                          <Marker
                            key={marker.id}
                            position={{ lat: marker.lat, lng: marker.lng }}
                            icon={getMarkerIcon(
                              marker.incidentType,
                              activeMarkerId === marker.id || stickyMarkerId === marker.id,
                            )}
                            onMouseOver={() => handleMarkerMouseOver(marker.id)}
                            onMouseOut={() => handleMarkerMouseOut(marker.id)}
                            onClick={() => {
                              const isSame = stickyMarkerId === marker.id;
                              setStickyMarkerId(isSame ? null : marker.id);
                              setSelectedReportForDetails(isSame ? null : marker);
                              setActiveMarkerId(isSame ? null : marker.id);
                            }}
                          />
                        ))}

                      {/* InfoWindow for active marker */}
                      {activeMarkerId && (
                        <InfoWindow
                          position={{
                            lat: reportMarkers.find(m => m.id === activeMarkerId)?.lat,
                            lng: reportMarkers.find(m => m.id === activeMarkerId)?.lng,
                          }}
                          onCloseClick={() => setActiveMarkerId(null)}
                          options={getInfoWindowOptions()}
                        >
                          <div
                            className="max-w-[140px] cursor-pointer p-0.5 float-right"
                            role="button"
                            tabIndex={0}
                          >
                            <div className="font-mono font-bold text-xs mb-1 text-blue-600">
                              SWARM-{reportMarkers.find(m => m.id === activeMarkerId)?.id}
                            </div>
                            <div className="font-semibold text-xs mb-1 text-gray-900">
                              {formatIncidentType
                                ? formatIncidentType(
                                    reportMarkers.find(m => m.id === activeMarkerId)?.incidentType,
                                  )
                                : reportMarkers.find(m => m.id === activeMarkerId)?.incidentType}
                            </div>
                            <div className="text-xs text-gray-600 mb-1">
                              {formatTimeForDisplay(
                                reportMarkers.find(m => m.id === activeMarkerId)
                                  ?.estimatedIncidentTime,
                                reportMarkers.find(m => m.id === activeMarkerId)?.timeUnknown,
                                reportMarkers.find(m => m.id === activeMarkerId)?.isDateRange,
                                reportMarkers.find(m => m.id === activeMarkerId)
                                  ?.incidentDateRangeStart,
                                reportMarkers.find(m => m.id === activeMarkerId)
                                  ?.incidentDateRangeEnd,
                              )}
                            </div>
                            <div className="text-xs text-gray-700 line-clamp-3 leading-tight mb-1">
                              {reportMarkers.find(m => m.id === activeMarkerId)?.description}
                            </div>
                            {reportMarkers.find(m => m.id === activeMarkerId)?.photoUrl ? (
                              <img
                                alt="Incident"
                                className="w-full h-16 object-cover rounded mb-1"
                                src={reportMarkers.find(m => m.id === activeMarkerId)?.photoUrl}
                              />
                            ) : (
                              <img
                                alt="Type"
                                className="w-full h-16 object-contain rounded mb-1 bg-gray-50"
                                src={
                                  getIncidentTypeIconUrl
                                    ? getIncidentTypeIconUrl(
                                        reportMarkers.find(m => m.id === activeMarkerId)
                                          ?.incidentType,
                                        60,
                                      )
                                    : reportMarkers.find(m => m.id === activeMarkerId)?.image_url ||
                                      'data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%20%20%20%20%20%20%3Cdefs%3E%0A%20%20%20%20%20%20%20%20%3Cfilter%20id%3D%22shadow%22%20x%3D%22-50%25%22%20y%3D%22-50%25%22%20width%3D%22200%25%22%20height%3D%22200%25%22%3E%0A%20%20%20%20%20%20%20%20%20%20%3CfeDropShadow%20dx%3D%220%22%20dy%3D%222%22%20stdDeviation%3D%222%22%20flood-color%3D%22rgba(0%2C0%2C0%2C0.2)%22%2F%3E%0A%20%20%20%20%20%20%20%20%3C%2Ffilter%3E%0A%20%20%20%20%20%20%3C%2Fdefs%3E%0A%20%20%20%20%20%20%3C!--%20Background%20circle%20--%3E%0A%20%20%20%20%20%20%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%2226%22%20fill%3D%22%23ef4444%22%20stroke%3D%22%23dc2626%22%20stroke-width%3D%222%22%20filter%3D%22url(%23shadow)%22%2F%3E%0A%20%20%20%20%20%20%3C!--%20Icon%20--%3E%0A%20%20%20%20%20%20%3Cpath%20d%3D%22M18.92%206.01C18.72%205.42%2018.16%205%2017.5%205h-11c-.66%200-1.22.42-1.42%201.01L3%2012v8c0%20.55.45%201%201%201h1c.55%200%201-.45%201-1v-1h12v1c0%20.55.45%201%201%201h1c.55%200%201-.45%201-1v-8l-2.08-5.99zM6.5%2016c-.83%200-1.5-.67-1.5-1.5S5.67%2013%206.5%2013s1.5.67%201.5%201.5S7.33%2016%206.5%2016zm11%200c-.83%200-1.5-.67-1.5-1.5s.67-1.5%201.5-1.5%201.5.67%201.5%201.5-.67%201.5-1.5%201.5zM5%2011l1.5-4.5h11L19%2011H5z%22%20fill%3D%22%23ffffff%22%20transform%3D%22translate(18%2C%2018)%20scale(1.5)%22%2F%3E%0A%20%20%20%20%3C%2Fsvg%3E%0A%20%20'
                                }
                              />
                            )}
                            <div className="text-xs text-gray-500">
                              {shortenAddress(
                                reportMarkers.find(m => m.id === activeMarkerId)?.location,
                              )}
                            </div>
                          </div>
                        </InfoWindow>
                      )}

                      {/* InfoWindow for sticky marker */}
                      {stickyMarkerId && stickyMarkerId !== activeMarkerId && (
                        <InfoWindow
                          position={{
                            lat: reportMarkers.find(m => m.id === stickyMarkerId)?.lat,
                            lng: reportMarkers.find(m => m.id === stickyMarkerId)?.lng,
                          }}
                          onCloseClick={() => setStickyMarkerId(null)}
                          options={getInfoWindowOptions()}
                        >
                          <div
                            className="max-w-[140px] cursor-pointer p-0.5"
                            role="button"
                            tabIndex={0}
                          >
                            <div className="font-mono font-bold text-xs mb-1 text-blue-600">
                              SWARM-{reportMarkers.find(m => m.id === stickyMarkerId)?.id}
                            </div>
                            <div className="font-semibold text-xs mb-1 text-gray-900">
                              {formatIncidentType
                                ? formatIncidentType(
                                    reportMarkers.find(m => m.id === stickyMarkerId)?.incidentType,
                                  )
                                : reportMarkers.find(m => m.id === stickyMarkerId)?.incidentType}
                            </div>
                            <div className="text-xs text-gray-600 mb-1">
                              {formatTimeForDisplay(
                                reportMarkers.find(m => m.id === stickyMarkerId)
                                  ?.estimatedIncidentTime,
                                reportMarkers.find(m => m.id === stickyMarkerId)?.timeUnknown,
                                reportMarkers.find(m => m.id === stickyMarkerId)?.isDateRange,
                                reportMarkers.find(m => m.id === stickyMarkerId)
                                  ?.incidentDateRangeStart,
                                reportMarkers.find(m => m.id === stickyMarkerId)
                                  ?.incidentDateRangeEnd,
                              )}
                            </div>
                            <div className="text-xs text-gray-700 line-clamp-3 leading-tight mb-1">
                              {reportMarkers.find(m => m.id === stickyMarkerId)?.description}
                            </div>
                            {reportMarkers.find(m => m.id === stickyMarkerId)?.photoUrl ? (
                              <img
                                alt="Incident"
                                className="w-full h-16 object-cover rounded mb-1"
                                src={reportMarkers.find(m => m.id === stickyMarkerId)?.photoUrl}
                              />
                            ) : (
                              <img
                                alt="Type"
                                className="w-full h-16 object-contain rounded mb-1 bg-gray-50"
                                src={
                                  getIncidentTypeIconUrl
                                    ? getIncidentTypeIconUrl(
                                        reportMarkers.find(m => m.id === stickyMarkerId)
                                          ?.incidentType,
                                        60,
                                      )
                                    : reportMarkers.find(m => m.id === stickyMarkerId)?.image_url ||
                                      'data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%20%20%20%20%20%20%3Cdefs%3E%0A%20%20%20%20%20%20%20%20%3Cfilter%20id%3D%22shadow%22%20x%3D%22-50%25%22%20y%3D%22-50%25%22%20width%3D%22200%25%22%20height%3D%22200%25%22%3E%0A%20%20%20%20%20%20%20%20%20%20%3CfeDropShadow%20dx%3D%220%22%20dy%3D%222%22%20stdDeviation%3D%222%22%20flood-color%3D%22rgba(0%2C0%2C0%2C0.2)%22%2F%3E%0A%20%20%20%20%20%20%20%20%3C%2Ffilter%3E%0A%20%20%20%20%20%20%3C%2Fdefs%3E%0A%20%20%20%20%20%20%3C!--%20Background%20circle%20--%3E%0A%20%20%20%20%20%20%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%2226%22%20fill%3D%22%23ef4444%22%20stroke%3D%22%23dc2626%22%20stroke-width%3D%222%22%20filter%3D%22url(%23shadow)%22%2F%3E%0A%20%20%20%20%20%20%3C!--%20Icon%20--%3E%0A%20%20%20%20%20%20%3Cpath%20d%3D%22M18.92%206.01C18.72%205.42%2018.16%205%2017.5%205h-11c-.66%200-1.22.42-1.42%201.01L3%2012v8c0%20.55.45%201%201%201h1c.55%200%201-.45%201-1v-1h12v1c0%20.55.45%201%201%201h1c.55%200%201-.45%201-1v-8l-2.08-5.99zM6.5%2016c-.83%200-1.5-.67-1.5-1.5S5.67%2013%206.5%2013s1.5.67%201.5%201.5S7.33%2016%206.5%2016zm11%200c-.83%200-1.5-.67-1.5-1.5s.67-1.5%201.5-1.5%201.5.67%201.5%201.5-.67%201.5-1.5%201.5zM5%2011l1.5-4.5h11L19%2011H5z%22%20fill%3D%22%23ffffff%22%20transform%3D%22translate(18%2C%2018)%20scale(1.5)%22%2F%3E%0A%20%20%20%20%3C%2Fsvg%3E%0A%20%20'
                                }
                              />
                            )}
                            <div className="text-xs text-gray-500">
                              {shortenAddress(
                                reportMarkers.find(m => m.id === stickyMarkerId)?.location,
                              )}
                            </div>
                          </div>
                        </InfoWindow>
                      )}
                      {/* User selected location marker */}
                      {selectedLocation && (
                        <Marker
                          position={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
                          icon={{
                            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="red"/>
                              </svg>
                            `)}`,
                            scaledSize: new window.google.maps.Size(24, 24),
                            anchor: new window.google.maps.Point(12, 24),
                          }}
                        />
                      )}
                    </GoogleMap>
                  )}
                </div>

                {/* Map Controls and Legend - Underneath the map */}
                <div className="mt-4 space-y-4">
                  {/* Map Controls */}
                  <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center sm:justify-between">
                    {/* Reset button */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                      <button
                        onClick={resetMapToDefault}
                        disabled={isResettingMap}
                        className="btn btn-secondary text-xs px-3 py-1 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        title="Reset map to default center and zoom with incident markers showing"
                        aria-label="Reset map to default center and zoom with incident markers showing"
                        data-testid="reset-map-button"
                      >
                        {isResettingMap ? (
                          <>
                            <span className="animate-spin inline-block w-3 h-3 border-2 border-gray-600 border-t-transparent rounded-full mr-1"></span>
                            Resetting...
                          </>
                        ) : (
                          'Reset Map'
                        )}
                      </button>
                      <span className="text-xs text-gray-500 sm:flex-1">
                        Returns to default view and shows incident markers
                      </span>
                    </div>

                    {/* Heatmap controls */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                      {/* Heatmap Radius Control */}
                      {showHeatmap && (
                        <div className="flex flex-col items-start sm:items-end sm:mr-4">
                          <div className="text-xs font-semibold text-gray-700 mb-1">
                            Radius: {heatmapRadius}m
                          </div>
                          <input
                            type="range"
                            min="20"
                            max="100"
                            value={heatmapRadius}
                            onChange={e => setHeatmapRadius(parseInt(e.target.value, 10))}
                            className="w-full sm:w-24 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            title={`Adjust heatmap radius: ${heatmapRadius}m`}
                          />
                        </div>
                      )}

                      {showHeatmap && (
                        <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer sm:mr-2">
                          <input
                            type="checkbox"
                            checked={showHeatmapClusters}
                            onChange={e => {
                              const enabled = e.target.checked;
                              setShowHeatmapClusters(enabled);
                              if (!enabled) {
                                setActiveMarkerId(null);
                                setStickyMarkerId(null);
                                setSelectedReportForDetails(null);
                              }
                            }}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            data-testid="heatmap-clusters-toggle"
                          />
                          Show cluster counts
                        </label>
                      )}

                      {/* Heatmap toggle button */}
                      <div className="flex flex-col items-start sm:items-end">
                        <button
                          onClick={() => setShowHeatmap(!showHeatmap)}
                          className="btn btn-secondary text-xs px-2 py-1 hover:bg-gray-200 transition-colors w-full sm:w-auto"
                          title={showHeatmap ? 'Switch to marker view' : 'Switch to heatmap view'}
                          aria-label={
                            showHeatmap ? 'Switch to marker view' : 'Switch to heatmap view'
                          }
                          data-testid="heatmap-toggle-button"
                        >
                          {showHeatmap ? 'Show Markers' : 'Show Heatmap'}
                        </button>
                        <span className="text-xs text-gray-500 mt-1 text-left sm:text-right">
                          {showHeatmap
                            ? showHeatmapClusters
                              ? 'Heatmap with cluster counts'
                              : 'Heatmap only (no cluster markers)'
                            : 'Visualise data with heatmap'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Map Legend */}
                  <div
                    className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                    data-testid="map-legend"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                      <h3 className="text-sm font-semibold text-gray-700">Incident Types</h3>
                      <span className="text-xs text-gray-500" data-testid="incidents-count">
                        {reportMarkers.length} incidents shown
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                      {/* Motor Vehicles */}
                      <div className="flex items-center gap-2" data-testid="legend-motor-vehicles">
                        <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-red-600 flex-shrink-0"></div>
                        <span className="text-gray-700">Motor Vehicles</span>
                      </div>

                      {/* Motorcycles */}
                      <div className="flex items-center gap-2" data-testid="legend-motorcycles">
                        <div className="w-4 h-4 rounded-full bg-orange-500 border-2 border-orange-600 flex-shrink-0"></div>
                        <span className="text-gray-700">Motorcycles</span>
                      </div>

                      {/* Trucks */}
                      <div className="flex items-center gap-2" data-testid="legend-trucks">
                        <div className="w-4 h-4 rounded-full bg-purple-500 border-2 border-purple-600 flex-shrink-0"></div>
                        <span className="text-gray-700">Trucks</span>
                      </div>

                      {/* Bicycles */}
                      <div className="flex items-center gap-2" data-testid="legend-bicycles">
                        <div className="w-4 h-4 rounded-full bg-blue-500 border-2 border-blue-600 flex-shrink-0"></div>
                        <span className="text-gray-700">Bicycles</span>
                      </div>

                      {/* Pedestrians */}
                      <div className="flex items-center gap-2" data-testid="legend-pedestrians">
                        <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-green-600 flex-shrink-0"></div>
                        <span className="text-gray-700">Pedestrians</span>
                      </div>

                      {/* Animals */}
                      <div className="flex items-center gap-2" data-testid="legend-animals">
                        <div className="w-4 h-4 rounded-full bg-amber-700 border-2 border-amber-800 flex-shrink-0"></div>
                        <span className="text-gray-700">Animals</span>
                      </div>

                      {/* Near Misses */}
                      <div className="flex items-center gap-2" data-testid="legend-near-misses">
                        <div className="w-4 h-4 rounded-full bg-yellow-500 border-2 border-yellow-600 flex-shrink-0"></div>
                        <span className="text-gray-700">Near Misses</span>
                      </div>

                      {/* Dangerous Driving */}
                      <div
                        className="flex items-center gap-2"
                        data-testid="legend-dangerous-driving"
                      >
                        <div className="w-4 h-4 rounded-full bg-red-700 border-2 border-red-800 flex-shrink-0"></div>
                        <span className="text-gray-700">Dangerous Driving</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Reports Table Section - Right Side */}
              <section
                className="swarm-reports-section bg-white rounded-lg shadow-sm border border-gray-200 p-3 lg:p-4 flex flex-col h-[600px] lg:h-[700px]"
                data-testid="reports-table-section"
              >
                <div className="flex flex-col gap-2 mb-3 flex-shrink-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <h2
                      className="text-lg lg:text-xl font-semibold text-gray-800"
                      data-testid="reports-table-title"
                    >
                      {copy.recentReports}
                    </h2>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0">
                      {incidentTypesWithData.length > 0 && (
                        <label className="min-w-0 sm:min-w-[12rem] sm:max-w-[16rem] flex-1 sm:flex-none">
                          <span className="sr-only">Filter by incident type</span>
                          <select
                            id="incident-type-filter"
                            data-testid="incident-type-filter"
                            value={typeFilter}
                            onChange={e => setTypeFilter(e.target.value)}
                            className="w-full min-h-[40px] text-sm border border-gray-300 rounded-md bg-white px-2 py-2 text-gray-700 touch-manipulation"
                          >
                            <option value="all">All incident types</option>
                            {incidentTypesWithData.map(type => (
                              <option key={type} value={type}>
                                {formatIncidentType(type)}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                      <div
                        className="text-sm text-gray-500 whitespace-nowrap"
                        data-testid="total-reports-count"
                      >
                        {sortedReports.length} {sortedReports.length === 1 ? 'report' : 'reports'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table Container with Scroll */}
                <div
                  className="overflow-x-auto overflow-y-auto flex-1"
                  ref={tableContainerRef}
                  data-testid="table-container"
                >
                  <table className="w-full border-collapse text-sm" data-testid="reports-table">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        {isAdminReportsTable ? (
                          <>
                            <SortableColumnHeader
                              column="ref"
                              label="Reference"
                              className="px-3 py-2 text-left font-medium text-gray-700 border-b"
                              testId="header-reference"
                              sort={reportSort}
                              onSort={handleReportSort}
                            />
                            <SortableColumnHeader
                              column="type"
                              label="Type"
                              className="px-3 py-2 text-left font-medium text-gray-700 border-b"
                              testId="header-type"
                              sort={reportSort}
                              onSort={handleReportSort}
                            />
                            <th
                              className="px-3 py-2 text-left font-medium text-gray-700 border-b"
                              data-testid="header-description"
                            >
                              Description
                            </th>
                            <th
                              className="px-3 py-2 text-left font-medium text-gray-700 border-b"
                              data-testid="header-emergency-services"
                            >
                              Emergency Services
                            </th>
                            <SortableColumnHeader
                              column="time"
                              label="Time"
                              className="px-3 py-2 text-left font-medium text-gray-700 border-b"
                              testId="header-time"
                              sort={reportSort}
                              onSort={handleReportSort}
                            />
                            <th
                              className="px-3 py-2 text-left font-medium text-gray-700 border-b"
                              data-testid="header-reporter"
                            >
                              Reporter
                            </th>
                            <th
                              className="px-3 py-2 text-left font-medium text-gray-700 border-b"
                              data-testid="header-status"
                            >
                              Status
                            </th>
                          </>
                        ) : (
                          <>
                            <SortableColumnHeader
                              column="type"
                              label="Incident type"
                              className="px-2 sm:px-3 py-2 text-left font-medium text-gray-700 border-b"
                              testId="header-type"
                              sort={reportSort}
                              onSort={handleReportSort}
                            />
                            <th
                              className="px-2 sm:px-3 py-2 text-left font-medium text-gray-700 border-b"
                              data-testid="header-description"
                            >
                              Description
                            </th>
                            <SortableColumnHeader
                              column="time"
                              label="Time"
                              className="px-2 sm:px-3 py-2 text-left font-medium text-gray-700 border-b"
                              testId="header-time"
                              sort={reportSort}
                              onSort={handleReportSort}
                            />
                            <SortableColumnHeader
                              column="ref"
                              label="Ref"
                              className="px-2 sm:px-3 py-2 text-left font-medium text-gray-500 border-b w-24"
                              testId="header-reference"
                              sort={reportSort}
                              onSort={handleReportSort}
                            />
                          </>
                        )}
                        <th
                          className="px-3 py-2 text-left font-medium text-gray-700 border-b"
                          data-testid="header-actions"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody data-testid="table-body">
                      {paginatedReports.map(report => (
                        <tr
                          key={report.id}
                          data-report-id={report.id}
                          data-testid={`table-row-${report.id}`}
                          className={`border-b border-gray-100 cursor-pointer transition-all duration-200 ${
                            selectedReportForDetails?.id === report.id
                              ? 'selected bg-blue-50 border-l-4 border-l-blue-500'
                              : ''
                          }`}
                          onClick={() => selectReportFromTable(report)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              selectReportFromTable(report);
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label={`View details for ${formatIncidentType(report.incidentType)} incident`}
                        >
                          {isAdminReportsTable ? (
                            <>
                              <td
                                className="px-3 py-2 text-xs font-mono font-medium text-blue-600"
                                data-testid={`cell-reference-${report.id}`}
                              >
                                SWARM{report.id}
                              </td>
                              <td
                                className="px-3 py-2 text-xs font-medium text-gray-900"
                                data-testid={`cell-type-${report.id}`}
                              >
                                {formatIncidentType(report.incidentType)}
                              </td>
                              <td
                                className="px-3 py-2 text-xs text-gray-700 max-w-[200px] truncate"
                                data-testid={`cell-description-${report.id}`}
                              >
                                {report.description}
                              </td>
                              <td
                                className="px-3 py-2 text-xs text-gray-600"
                                data-testid={`cell-emergency-services-${report.id}`}
                              >
                                {report.emergencyServicesAttended || 'Not specified'}
                                {report.emergencyServicesAttended === 'yes' && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {report.policeAttended && 'Police '}
                                    {report.ambulanceAttended && 'Ambulance '}
                                    {report.fireBrigadeAttended && 'Fire Brigade'}
                                  </div>
                                )}
                              </td>
                              <td
                                className="px-3 py-2 text-xs text-gray-600"
                                data-testid={`cell-time-${report.id}`}
                              >
                                {formatTimeForDisplay(
                                  report.estimatedIncidentTime,
                                  report.timeUnknown,
                                  report.isDateRange,
                                  report.incidentDateRangeStart,
                                  report.incidentDateRangeEnd,
                                )}
                              </td>
                              <td
                                className="px-3 py-2 text-xs text-gray-600"
                                data-testid={`cell-reporter-${report.id}`}
                              >
                                {getAnonymizedReporter(user, user?.isAdmin, report)}
                              </td>
                              <td
                                className="px-3 py-2 text-xs"
                                data-testid={`cell-status-${report.id}`}
                              >
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    report.isApproved
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}
                                  data-testid={`status-text-${report.id}`}
                                >
                                  {report.isApproved ? 'Approved' : 'Pending'}
                                </span>
                              </td>
                            </>
                          ) : (
                            <>
                              <td
                                className="px-2 sm:px-3 py-2 text-sm font-semibold text-gray-900"
                                data-testid={`cell-type-${report.id}`}
                              >
                                {formatIncidentType(report.incidentType)}
                              </td>
                              <td
                                className="px-2 sm:px-3 py-2 text-sm text-gray-800 max-w-[240px] lg:max-w-none"
                                data-testid={`cell-description-${report.id}`}
                              >
                                <span className="line-clamp-2">{report.description}</span>
                              </td>
                              <td
                                className="px-2 sm:px-3 py-2 text-sm text-gray-600 whitespace-nowrap"
                                data-testid={`cell-time-${report.id}`}
                              >
                                {formatTimeForDisplay(
                                  report.estimatedIncidentTime,
                                  report.timeUnknown,
                                  report.isDateRange,
                                  report.incidentDateRangeStart,
                                  report.incidentDateRangeEnd,
                                )}
                              </td>
                              <td
                                className="px-2 sm:px-3 py-2 text-xs font-mono text-gray-400"
                                data-testid={`cell-reference-${report.id}`}
                              >
                                SWARM-{report.id}
                              </td>
                            </>
                          )}
                          <td
                            className="px-3 py-2 text-xs"
                            data-testid={`cell-actions-${report.id}`}
                          >
                            <div
                              className="flex flex-row gap-2"
                              data-testid={`actions-container-${report.id}`}
                            >
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  selectReportFromTable(report);
                                }}
                                className="btn btn-secondary text-xs px-2 py-1 hover:bg-gray-200 transition-colors"
                                data-testid={`view-button-${report.id}`}
                              >
                                View
                              </button>
                              {/* Edit button - Only show for non-admin users editing their own pending reports */}
                              {user &&
                                !user.isAdmin &&
                                !user.isSuperAdmin &&
                                report.userEmail === user.email &&
                                !report.isApproved && (
                                  <button
                                    onClick={e => {
                                      e.stopPropagation();
                                      setEditingReport(report);
                                    }}
                                    className="btn btn-primary text-xs px-2 py-1 hover:bg-blue-700 transition-colors"
                                    data-testid={`edit-button-${report.id}`}
                                  >
                                    Edit
                                  </button>
                                )}
                              {/* Approval buttons - Hidden from main screen as requested */}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination - Compact prev/next so reports stay visible on mobile */}
                {totalPages > 1 && (
                  <nav
                    className="mt-2 flex items-center justify-center gap-3 flex-shrink-0"
                    data-testid="pagination"
                    aria-label="Reports pagination"
                  >
                    <button
                      type="button"
                      onClick={() => setCurrentTablePage(p => Math.max(1, p - 1))}
                      disabled={currentTablePage === 1}
                      className="text-xs text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed px-1 py-0.5"
                      aria-label="Previous page"
                      data-testid="page-button-prev"
                    >
                      ‹ Prev
                    </button>
                    <span
                      className="text-xs text-gray-500 tabular-nums"
                      data-testid="page-indicator"
                    >
                      {currentTablePage} / {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCurrentTablePage(p => Math.min(totalPages, p + 1))}
                      disabled={currentTablePage === totalPages}
                      className="text-xs text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed px-1 py-0.5"
                      aria-label="Next page"
                      data-testid="page-button-next"
                    >
                      Next ›
                    </button>
                  </nav>
                )}
              </section>
            </div>

            {/* Report Details Section - Beneath Map and Table */}
            {selectedReportForDetails && (
              <section
                ref={detailViewRef}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:p-6 mb-6"
                data-testid="report-details-section"
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                  <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
                    Incident Details: {formatIncidentType(selectedReportForDetails.incidentType)}
                  </h2>
                  <button
                    onClick={() => setSelectedReportForDetails(null)}
                    className="btn btn-secondary"
                    data-testid="close-details-button"
                  >
                    Close Details
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                  {/* Basic Information */}
                  <div className="space-y-4" data-testid="basic-information">
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
                      Basic Information
                    </h3>
                    <div className="space-y-2">
                      <p
                        data-testid="incident-reference"
                        className="font-mono font-bold text-blue-600"
                      >
                        <strong>Reference:</strong> SWARM{selectedReportForDetails.id}
                      </p>
                      <p data-testid="incident-type">
                        <strong>Incident Type:</strong>{' '}
                        {formatIncidentType(selectedReportForDetails.incidentType)}
                      </p>
                      {vehiclesInfo && (
                        <p data-testid="vehicles-involved">
                          <strong>Vehicles Involved:</strong> {vehiclesInfo.value}
                          {vehiclesInfo.isDefault && (
                            <span className="text-xs text-gray-500 ml-2">(default)</span>
                          )}
                        </p>
                      )}
                      <p data-testid="incident-location">
                        <strong>Location:</strong> {selectedReportForDetails.location}
                      </p>
                      <p data-testid="incident-description">
                        <strong>Description:</strong> {selectedReportForDetails.description}
                      </p>
                      <p data-testid="incident-reporter">
                        <strong>Reporter:</strong>{' '}
                        {getAnonymizedReporter(user, user?.isAdmin, selectedReportForDetails)}
                      </p>
                      {user?.isAdmin && (
                        <p data-testid="incident-submitter-ip">
                          <strong>Submitter IP:</strong>{' '}
                          <span className="font-mono">
                            {selectedReportForDetails.submitterIp || 'Not recorded'}
                          </span>
                        </p>
                      )}
                      <p data-testid="incident-time">
                        <strong>Time:</strong>{' '}
                        {formatTimeForDisplay(
                          selectedReportForDetails.estimatedIncidentTime,
                          selectedReportForDetails.timeUnknown,
                          selectedReportForDetails.isDateRange,
                          selectedReportForDetails.incidentDateRangeStart,
                          selectedReportForDetails.incidentDateRangeEnd,
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Emergency Services Information */}
                  <div className="space-y-4" data-testid="emergency-services-information">
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
                      Emergency Services
                    </h3>
                    <div className="space-y-2">
                      <p data-testid="emergency-services-attended">
                        <strong>Emergency Services Attended:</strong>{' '}
                        {selectedReportForDetails.emergencyServicesAttended || 'Not specified'}
                      </p>
                      {selectedReportForDetails.emergencyServicesAttended === 'yes' && (
                        <div className="ml-4">
                          <p>
                            <strong>Services:</strong>
                          </p>
                          <ul className="list-disc list-inside ml-4">
                            {selectedReportForDetails.policeAttended && <li>Police</li>}
                            {selectedReportForDetails.ambulanceAttended && (
                              <li>Ambulance/Paramedics</li>
                            )}
                            {selectedReportForDetails.fireBrigadeAttended && <li>Fire Brigade</li>}
                          </ul>
                        </div>
                      )}
                      {selectedReportForDetails.policeAttended &&
                        selectedReportForDetails.policeReportTime && (
                          <p data-testid="police-report-time">
                            <strong>Police Report Time:</strong>{' '}
                            {formatTimeForDisplay(selectedReportForDetails.policeReportTime)}
                          </p>
                        )}
                      {selectedReportForDetails.policeAttended &&
                        selectedReportForDetails.policeOfficerName && (
                          <p data-testid="police-officer-name">
                            <strong>Officer Name:</strong>{' '}
                            {selectedReportForDetails.policeOfficerName}
                          </p>
                        )}
                      {selectedReportForDetails.policeAttended &&
                        selectedReportForDetails.policeReportNumber && (
                          <p data-testid="police-report-number">
                            <strong>Report Number:</strong>{' '}
                            {selectedReportForDetails.policeReportNumber}
                          </p>
                        )}
                    </div>
                  </div>

                  {/* Photos */}
                  <div className="space-y-4" data-testid="photos-section">
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Photos</h3>
                    {(() => {
                      const photoCaptions = parseJsonArray(selectedReportForDetails.photoCaptions);
                      return selectedReportForDetails.photoUrls &&
                        Array.isArray(selectedReportForDetails.photoUrls) &&
                        selectedReportForDetails.photoUrls.length > 0 ? (
                        <div className="space-y-3">
                          {selectedReportForDetails.photoUrls.length === 1 ? (
                            // Single photo display
                            <div className="space-y-2">
                              <div
                                className="cursor-pointer"
                                onClick={() => {
                                  const photos = [
                                    {
                                      url: getSafeFileUrl(selectedReportForDetails.photoUrls[0]),
                                      caption: photoCaptions[0] || '',
                                    },
                                  ];
                                  openPreviewModal(
                                    getSafeFileUrl(selectedReportForDetails.photoUrls[0]),
                                    photos[0].caption,
                                    photos,
                                    0,
                                  );
                                }}
                              >
                                <img
                                  src={getSafeFileUrl(selectedReportForDetails.photoUrls[0])}
                                  alt="Incident photo"
                                  className="w-full h-48 object-cover rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                                  data-testid="incident-photo"
                                />
                              </div>
                              {photoCaptions[0] && (
                                <p className="text-sm text-gray-600 italic">{photoCaptions[0]}</p>
                              )}
                            </div>
                          ) : (
                            // Photo gallery for multiple photos
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {Array.isArray(selectedReportForDetails.photoUrls) &&
                                selectedReportForDetails.photoUrls.map((photoUrl, index) => (
                                  <div
                                    key={index}
                                    className="relative group cursor-pointer"
                                    onClick={() => {
                                      // Open lightbox with navigation
                                      const photos = Array.isArray(
                                        selectedReportForDetails.photoUrls,
                                      )
                                        ? selectedReportForDetails.photoUrls.map((url, idx) => ({
                                            url: getSafeFileUrl(url),
                                            caption: photoCaptions[idx] || '',
                                          }))
                                        : [];
                                      openPreviewModal(
                                        getSafeFileUrl(photoUrl),
                                        photos[index].caption,
                                        photos,
                                        index,
                                      );
                                    }}
                                  >
                                    <img
                                      src={getSafeFileUrl(photoUrl)}
                                      alt={`Incident photo ${index + 1}`}
                                      className="w-full h-32 object-cover rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                                      data-testid={`incident-photo-${index}`}
                                    />
                                    {photoCaptions[index] && (
                                      <p
                                        className="text-xs text-gray-600 italic mt-1 overflow-hidden"
                                        style={{
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                        }}
                                      >
                                        {photoCaptions[index]}
                                      </p>
                                    )}
                                    {/* Overlay for click indication */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded-lg flex items-center justify-center">
                                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                        <svg
                                          className="w-6 h-6 text-white"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                          />
                                        </svg>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          )}
                          <p className="text-sm text-gray-500">
                            {selectedReportForDetails.photoUrls.length === 1
                              ? '1 photo - Click to view full size'
                              : `${selectedReportForDetails.photoUrls.length} photos - Click any photo to view in gallery`}
                          </p>
                        </div>
                      ) : (
                        // No photos - show incident type icon
                        <div className="space-y-2">
                          <img
                            src={getIncidentTypeIconUrl(selectedReportForDetails.incidentType, 192)}
                            alt={`${formatIncidentType(selectedReportForDetails.incidentType)} incident`}
                            className="w-full h-48 object-cover rounded-lg shadow-sm"
                            data-testid="incident-photo"
                          />
                          <p className="text-sm text-gray-500 italic">No photos available</p>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Extra Notes Section */}
                {selectedReportForDetails && (
                  <div className="mt-8" data-testid="extra-notes-section">
                    {isInitializing ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
                            <div className="w-32 h-6 bg-gray-200 rounded animate-pulse"></div>
                            <div className="w-8 h-4 bg-gray-200 rounded animate-pulse"></div>
                          </div>
                        </div>
                        <div className="text-center py-8 text-gray-500">
                          <div className="w-12 h-12 bg-gray-200 rounded-full mx-auto mb-2 animate-pulse"></div>
                          <div className="w-48 h-4 bg-gray-200 rounded mx-auto animate-pulse"></div>
                        </div>
                      </div>
                    ) : (
                      <ExtraNotesManager
                        reportId={selectedReportForDetails.id}
                        user={user}
                        onNotesUpdate={() => {
                          // Refresh the reports list if needed
                          // This could be used to refresh reports data after notes are updated
                        }}
                      />
                    )}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 p-4 mt-auto" data-testid="footer">
        <div className="container mx-auto text-center">
          <p className="text-gray-600">
            © 2026{' '}
            <a
              href="https://anthemic-developments.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700"
            >
              anthemic-developments.com
            </a>
          </p>
        </div>
      </footer>

      {/* Modals */}
      {renderCropModal()}
      {renderPreviewModal()}
      {renderImageEditor()}
      <MapPicker
        isOpen={showMapPicker}
        onClose={closeMapPopup}
        onConfirm={handleMapPickerConfirm}
        initialLocation={
          form.coordinates && form.location
            ? {
                ...(() => {
                  // Handle both string and object formats for coordinates
                  let lat, lng;
                  if (typeof form.coordinates === 'string') {
                    const match = form.coordinates.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
                    if (!match) return null;
                    lat = parseFloat(match[1]);
                    lng = parseFloat(match[2]);
                  } else if (
                    form.coordinates &&
                    typeof form.coordinates === 'object' &&
                    form.coordinates.lat !== undefined &&
                    form.coordinates.lng !== undefined
                  ) {
                    lat = form.coordinates.lat;
                    lng = form.coordinates.lng;
                  } else {
                    return null;
                  }

                  // Validate that we have valid numbers
                  if (
                    typeof lat !== 'number' ||
                    typeof lng !== 'number' ||
                    isNaN(lat) ||
                    isNaN(lng)
                  ) {
                    console.warn('Invalid coordinates for MapPicker:', form.coordinates);
                    return null;
                  }

                  return { lat, lng };
                })(),
                address: form.location,
                coords:
                  typeof form.coordinates === 'string'
                    ? form.coordinates
                    : `${form.coordinates?.lat}, ${form.coordinates?.lng}`,
              }
            : null
        }
        activeArea={activeArea}
        description={form.description || ''}
      />

      {/* Report Form Modal */}
      {showReportForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 lg:p-4">
          <div className="bg-white rounded-2xl shadow-large p-4 lg:p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
            {/* Modal Header with navigation for editing */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 lg:mb-6 pr-8 lg:pr-12">
              <div className="flex items-center gap-2 lg:gap-4">
                <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
                  {editingReport ? 'Edit Incident Report' : 'Report Incident'}
                </h2>
                {editingReport && (
                  <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                    {/* X of Y indicator for editing */}
                    {(() => {
                      const idx = reports.findIndex(r => r.id === editingReport.id);
                      return `${idx + 1} of ${reports.length}`;
                    })()}
                  </span>
                )}
              </div>
              {/* Navigation buttons for editing */}
              {editingReport && (
                <div className="flex items-center gap-2 lg:gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      const idx = reports.findIndex(r => r.id === editingReport.id);
                      if (idx > 0) setEditingReport(reports[idx - 1]);
                    }}
                    disabled={reports.findIndex(r => r.id === editingReport.id) === 0}
                    className={`btn btn-secondary text-sm px-4 py-2 ${reports.findIndex(r => r.id === editingReport.id) === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'}`}
                    aria-label="Previous report"
                  >
                    ← Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const idx = reports.findIndex(r => r.id === editingReport.id);
                      if (idx < reports.length - 1) setEditingReport(reports[idx + 1]);
                    }}
                    disabled={
                      reports.findIndex(r => r.id === editingReport.id) === reports.length - 1
                    }
                    className={`btn btn-secondary text-sm px-4 py-2 ${reports.findIndex(r => r.id === editingReport.id) === reports.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'}`}
                    aria-label="Next report"
                  >
                    Next →
                  </button>
                </div>
              )}
              <button
                onClick={handleCloseReportForm}
                className="absolute top-3 lg:top-4 right-3 lg:right-4 text-gray-400 hover:text-gray-600 text-xl lg:text-2xl font-bold bg-transparent border-none cursor-pointer"
                aria-label="Close edit modal"
              >
                ×
              </button>
            </div>

            {/* Prominent Success Message */}
            {successMessage && (
              <div
                className="mb-6 p-4 rounded-lg border border-green-300 bg-green-50 text-green-800 font-semibold text-center shadow focus:outline-none focus:ring-2 focus:ring-green-400"
                role="status"
                aria-live="polite"
                tabIndex={0}
                style={{ position: 'sticky', top: 0, zIndex: 30 }}
              >
                {successMessage}
              </div>
            )}

            {Object.keys(formErrors).length > 0 && (
              <div
                ref={errorSummaryRef}
                className="validation-summary mb-4"
                tabIndex={-1}
                role="alert"
                aria-live="assertive"
              >
                <h3>Please fix the following errors:</h3>
                <ul>
                  {Object.entries(formErrors).map(([fieldName, error]) => (
                    <li key={fieldName}>
                      <strong>
                        {fieldName
                          .replace(/([A-Z])/g, ' $1')
                          .replace(/^./, str => str.toUpperCase())}
                        :
                      </strong>{' '}
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
              {/* Privacy and Moderation Notice - Show only for anonymous users */}
              {!user && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="text-sm text-blue-800">
                      <h4 className="font-semibold mb-2">Important Information</h4>
                      <ul className="space-y-1">
                        <li>
                          • <strong>Moderation:</strong> Your report will be reviewed by
                          administrators before appearing on the public map
                        </li>
                        <li>
                          • <strong>Privacy:</strong> Your personal details (name, email, phone)
                          will not be visible to the public
                        </li>
                        <li>
                          • <strong>Review Process:</strong> Reports are typically reviewed within
                          24-48 hours
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Incident Details Section */}
              <div className="form-section">
                <h3 className="text-lg lg:text-xl font-semibold text-primary-800 mb-3 lg:mb-4">
                  Incident Details
                </h3>
                {/* Incident Type */}
                <div className="form-group">
                  <label className="form-label required">Incident Type</label>
                  <select
                    name="incidentType"
                    value={form.incidentType || ''}
                    onChange={e => setForm(prev => ({ ...prev, incidentType: e.target.value }))}
                    className="form-input"
                    required
                  >
                    <option value="">Select incident type...</option>
                    {INCIDENT_TYPE_GROUPS.map(group => (
                      <optgroup key={group.label} label={group.label}>
                        {group.options.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                {/* Description */}
                <div className="form-group">
                  <label className="form-label required">Description</label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                    className="form-input"
                    placeholder="Describe what happened..."
                    required
                    rows={4}
                  />
                </div>
                {/* Photos Section */}
                <div className="form-group mb-6">
                  <label className="form-label">Photos (optional):</label>
                  {form.photos.length > 0 && (
                    <div className="text-sm text-gray-600 mb-2">
                      {form.photos.length} photo{form.photos.length !== 1 ? 's' : ''} uploaded
                    </div>
                  )}
                  <div className="flex flex-wrap gap-4 mb-2">
                    {form.photos.map((photo, idx) => (
                      <div key={idx} className="relative group flex flex-col items-center">
                        <img
                          src={
                            photo.file ? URL.createObjectURL(photo.file) : getSafeFileUrl(photo.url)
                          }
                          alt={`Photo ${idx + 1}`}
                          className="w-24 h-24 object-cover rounded border mb-1"
                        />
                        <div className="flex gap-1 mb-1">
                          <button
                            type="button"
                            onClick={() => {
                              const photoUrl = photo.file
                                ? URL.createObjectURL(photo.file)
                                : getSafeFileUrl(photo.url);
                              openPreviewModal(
                                photoUrl,
                                photo.caption,
                                form.photos.map(p => ({
                                  url: p.file ? URL.createObjectURL(p.file) : getSafeFileUrl(p.url),
                                  caption: p.caption,
                                })),
                                idx,
                              );
                            }}
                            className="btn btn-xs btn-secondary"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => openImageEditor(photo.file || photo.url, idx)}
                            className="btn btn-xs btn-primary"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(idx)}
                            className="btn btn-xs btn-error"
                            aria-label="Remove image"
                          >
                            ×
                          </button>
                        </div>
                        <input
                          type="text"
                          placeholder="Caption (optional)"
                          value={photo.caption || ''}
                          onChange={e => handleCaptionChange(idx, e.target.value)}
                          className="mt-2 w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                    ))}
                  </div>
                  <div
                    className={`photo-upload-area mt-2 border-2 border-dashed rounded-lg p-4 lg:p-6 transition-colors ${
                      form.photos.length >= maxPhotos
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                        : dragActive
                          ? 'border-primary-500 bg-primary-50 cursor-pointer'
                          : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50 cursor-pointer'
                    }`}
                    onClick={e => {
                      console.log('Photo upload area clicked');
                      if (form.photos.length >= maxPhotos) {
                        e.preventDefault();
                        e.stopPropagation();
                        setPhotoError(
                          `You have reached the ${maxPhotos} photo limit. Remove a photo to add another.`,
                        );
                        console.log('Max photos reached, preventing click');
                      }
                    }}
                    onDrop={e => {
                      if (form.photos.length >= maxPhotos) return;
                      e.preventDefault();
                      e.stopPropagation();
                      handleDrop(e);
                    }}
                    onDragOver={e => {
                      if (form.photos.length >= maxPhotos) return;
                      e.preventDefault();
                      e.stopPropagation();
                      setDragActive(true);
                    }}
                    onDragEnter={e => {
                      if (form.photos.length >= maxPhotos) return;
                      e.preventDefault();
                      e.stopPropagation();
                      setDragActive(true);
                    }}
                    onDragLeave={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Only set dragActive to false if we're leaving the drop zone entirely
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        setDragActive(false);
                      }
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      name="photos"
                      multiple
                      accept="image/*"
                      onChange={handleChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      style={{ zIndex: 1 }}
                    />
                    <div className="text-center">
                      <div className="mb-4">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <p className="text-base lg:text-lg text-gray-600 mb-2">
                        {dragActive ? 'Drop photos here' : 'Drop photos here or click to upload'}
                      </p>
                      <p className="text-sm text-gray-500">
                        <strong>Multiple photos supported!</strong> Maximum {maxPhotos} photos,{' '}
                        {maxFileSizeMB}MB each
                        {form.photos.length > 0 && (
                          <span className="block mt-1">
                            {form.photos.length}/{maxPhotos} photos uploaded
                          </span>
                        )}
                      </p>
                      {uploading && uploadProgress > 0 && (
                        <div className="mt-4">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${uploadProgress}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-600 mt-1 text-center">
                            Uploading... {uploadProgress}%
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  {photoError && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg
                            className="h-5 w-5 text-red-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">Photo Upload Error</h3>
                          <div className="mt-2 text-sm text-red-700">
                            <p>{photoError}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Location */}
                <div className="form-group">
                  <label className="form-label required">Location</label>
                  <input
                    type="text"
                    name="location"
                    value={form.location}
                    onChange={e => setForm(prev => ({ ...prev, location: e.target.value }))}
                    className="form-input"
                    placeholder="Enter location description or click 'Select on Map' below"
                    required
                  />
                  <div className="flex flex-col sm:flex-row gap-2 mt-2">
                    <button
                      type="button"
                      onClick={openMapPopup}
                      className="btn btn-primary flex items-center gap-2 px-4 py-2 text-sm font-medium"
                      aria-label="Click to select location on interactive map"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      📍 Click to Select Location on Map
                    </button>
                    <div className="text-xs text-gray-500 mt-1 sm:mt-0 sm:ml-2 flex items-center">
                      💡{' '}
                      <span className="ml-1">
                        Click the button to open an interactive map where you can click to pinpoint
                        the exact incident location
                      </span>
                    </div>
                  </div>
                </div>
                {form.coordinates && (
                  <div className="form-group">
                    <span className="text-sm text-gray-600">
                      <strong>Coordinates:</strong>{' '}
                      {typeof form.coordinates === 'string'
                        ? form.coordinates
                        : `${form.coordinates.lat}, ${form.coordinates.lng}`}
                    </span>
                  </div>
                )}
              </div>

              {/* Emergency Services Section */}
              <div className="form-section">
                <h3 className="text-lg font-semibold text-primary-800 mb-3 lg:mb-4">
                  Emergency Services
                </h3>
                <div className="form-group">
                  <label className="form-label">Did police and/or emergency services attend?</label>
                  <select
                    name="emergencyServicesAttended"
                    value={form.emergencyServicesAttended}
                    onChange={e =>
                      setForm(prev => ({ ...prev, emergencyServicesAttended: e.target.value }))
                    }
                    className="form-input"
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="dont-know">Don't know</option>
                  </select>
                </div>
                {form.emergencyServicesAttended === 'yes' && (
                  <div className="form-section-nested">
                    <div className="form-group">
                      <label className="form-label">Which services attended?</label>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={form.policeAttended}
                            onChange={e =>
                              setForm(prev => ({ ...prev, policeAttended: e.target.checked }))
                            }
                            className="mr-2"
                          />
                          Police
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={form.ambulanceAttended}
                            onChange={e =>
                              setForm(prev => ({ ...prev, ambulanceAttended: e.target.checked }))
                            }
                            className="mr-2"
                          />
                          Ambulance/Paramedics
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={form.fireBrigadeAttended}
                            onChange={e =>
                              setForm(prev => ({ ...prev, fireBrigadeAttended: e.target.checked }))
                            }
                            className="mr-2"
                          />
                          Fire Brigade
                        </label>
                      </div>
                    </div>
                    {form.policeAttended && (
                      <>
                        <div className="form-group">
                          <label className="form-label">Police Report Time</label>
                          <input
                            type="datetime-local"
                            name="policeReportTime"
                            value={form.policeReportTime}
                            onChange={e =>
                              setForm(prev => ({ ...prev, policeReportTime: e.target.value }))
                            }
                            className="form-input"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Police Officer Name</label>
                          <input
                            type="text"
                            name="policeOfficerName"
                            value={form.policeOfficerName}
                            onChange={e =>
                              setForm(prev => ({ ...prev, policeOfficerName: e.target.value }))
                            }
                            className="form-input"
                            placeholder="Officer's name"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Police Report Number</label>
                          <input
                            type="text"
                            name="policeReportNumber"
                            value={form.policeReportNumber}
                            onChange={e =>
                              setForm(prev => ({ ...prev, policeReportNumber: e.target.value }))
                            }
                            className="form-input"
                            placeholder="Report number"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Reporter Details Section */}
              <div className="form-section">
                <h3 className="text-lg font-semibold text-primary-800 mb-3 lg:mb-4">
                  Reporter Details
                </h3>
                <div className="form-group">
                  <label className="form-label required">Your Name</label>
                  <input
                    type="text"
                    name="reporterName"
                    value={form.reporterName}
                    onChange={e => setForm(prev => ({ ...prev, reporterName: e.target.value }))}
                    className="form-input"
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Your Phone Number</label>
                  <input
                    type="tel"
                    name="reporterNumber"
                    value={form.reporterNumber}
                    onChange={e => setForm(prev => ({ ...prev, reporterNumber: e.target.value }))}
                    className="form-input"
                    placeholder="Your phone number"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label required">Your Email</label>
                  <input
                    type="email"
                    name="reporterEmail"
                    value={form.reporterEmail}
                    onChange={e => setForm(prev => ({ ...prev, reporterEmail: e.target.value }))}
                    className="form-input"
                    placeholder="Your email address"
                    required
                  />
                </div>
              </div>

              {/* Additional Incident Details Section */}
              <div className="form-section">
                <h3 className="text-lg font-semibold text-primary-800 mb-3 lg:mb-4">
                  Additional Incident Details
                </h3>
                <div className="form-group">
                  <label className="form-label">Did you witness this incident?</label>
                  <select
                    name="witnessedIncident"
                    value={form.witnessedIncident}
                    onChange={e =>
                      setForm(prev => ({ ...prev, witnessedIncident: e.target.value }))
                    }
                    className="form-input"
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes, I witnessed it</option>
                    <option value="after">Witnessed after the incident</option>
                    <option value="no">No, I was told about it</option>
                    <option value="unsure">Unsure</option>
                  </select>
                </div>
                {form.witnessedIncident === 'after' && (
                  <div className="form-group">
                    <label className="form-label">Please describe the evidence you observed</label>
                    <textarea
                      name="evidenceDescription"
                      value={form.evidenceDescription}
                      onChange={e =>
                        setForm(prev => ({ ...prev, evidenceDescription: e.target.value }))
                      }
                      className="form-input"
                      placeholder="e.g., Damaged traffic light pole, skid marks on road, debris scattered across intersection"
                      rows="3"
                    />
                    <div className="form-help-text">
                      Describe what evidence you saw that indicates an incident occurred
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Estimated Incident Time</label>
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="checkbox"
                      id="timeUnknown"
                      name="timeUnknown"
                      checked={form.timeUnknown || false}
                      onChange={e => {
                        const isTimeUnknown = e.target.checked;
                        setForm(prev => ({
                          ...prev,
                          timeUnknown: isTimeUnknown,
                          // If switching to time unknown, clear the time portion
                          estimatedIncidentTime:
                            isTimeUnknown && prev.estimatedIncidentTime
                              ? prev.estimatedIncidentTime.split('T')[0]
                              : prev.estimatedIncidentTime,
                        }));
                      }}
                      className="form-checkbox"
                    />
                    <label htmlFor="timeUnknown" className="text-sm text-gray-600">
                      Time is unknown (date only)
                    </label>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="checkbox"
                      id="isDateRange"
                      name="isDateRange"
                      checked={form.isDateRange || false}
                      onChange={e => {
                        const isDateRange = e.target.checked;
                        setForm(prev => ({
                          ...prev,
                          isDateRange,
                          timeUnknown: isDateRange ? true : prev.timeUnknown, // Date ranges are always time unknown
                          // Clear range fields if unchecking
                          incidentDateRangeStart: isDateRange ? prev.incidentDateRangeStart : '',
                          incidentDateRangeEnd: isDateRange ? prev.incidentDateRangeEnd : '',
                        }));
                      }}
                      className="form-checkbox"
                    />
                    <label htmlFor="isDateRange" className="text-sm text-gray-600">
                      Date is a range (max 5 days)
                    </label>
                  </div>
                  {form.isDateRange ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Range Start Date
                        </label>
                        <input
                          type="date"
                          name="incidentDateRangeStart"
                          value={form.incidentDateRangeStart}
                          onChange={e =>
                            setForm(prev => ({ ...prev, incidentDateRangeStart: e.target.value }))
                          }
                          className={`form-input ${formErrors.incidentDateRangeStart ? 'border-red-500' : ''}`}
                        />
                        {formErrors.incidentDateRangeStart && (
                          <div className="text-red-500 text-sm mt-1">
                            {formErrors.incidentDateRangeStart}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Range End Date
                        </label>
                        <input
                          type="date"
                          name="incidentDateRangeEnd"
                          value={form.incidentDateRangeEnd}
                          onChange={e =>
                            setForm(prev => ({ ...prev, incidentDateRangeEnd: e.target.value }))
                          }
                          className={`form-input ${formErrors.incidentDateRangeEnd ? 'border-red-500' : ''}`}
                        />
                        {formErrors.incidentDateRangeEnd && (
                          <div className="text-red-500 text-sm mt-1">
                            {formErrors.incidentDateRangeEnd}
                          </div>
                        )}
                      </div>
                      <div className="form-help-text">
                        Select the date range when the incident occurred (maximum 5 days)
                      </div>
                    </div>
                  ) : (
                    <>
                      <input
                        type={form.timeUnknown ? 'date' : 'datetime-local'}
                        name="estimatedIncidentTime"
                        value={form.estimatedIncidentTime}
                        onChange={e =>
                          setForm(prev => ({ ...prev, estimatedIncidentTime: e.target.value }))
                        }
                        className="form-input"
                      />
                      <div className="form-help-text">
                        {form.timeUnknown
                          ? 'Select the date when the incident occurred (time is unknown)'
                          : 'Select the date and time when the incident occurred'}
                      </div>
                    </>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Were there any injuries?</label>
                  <select
                    name="injuries"
                    value={form.injuries}
                    onChange={e => setForm(prev => ({ ...prev, injuries: e.target.value }))}
                    className="form-input"
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="unsure">Unsure</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Number of Vehicles Involved</label>
                  <input
                    type="number"
                    name="vehiclesInvolved"
                    value={form.vehiclesInvolved}
                    onChange={e => setForm(prev => ({ ...prev, vehiclesInvolved: e.target.value }))}
                    className="form-input"
                    placeholder="Enter number of vehicles"
                    min="1"
                  />
                  <div className="form-help-text">Optional. Leave blank if unknown.</div>
                </div>
                {form.injuries === 'yes' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Number of Injuries</label>
                      <input
                        type="number"
                        name="injuryCount"
                        value={form.injuryCount}
                        onChange={e => setForm(prev => ({ ...prev, injuryCount: e.target.value }))}
                        className="form-input"
                        placeholder="Number of people injured"
                        min="1"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Injury Notes</label>
                      <textarea
                        name="injuryNotes"
                        value={form.injuryNotes}
                        onChange={e => setForm(prev => ({ ...prev, injuryNotes: e.target.value }))}
                        className="form-input"
                        placeholder="Describe the injuries..."
                        rows={3}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={handleCloseReportForm}
                  className="btn btn-secondary order-2 sm:order-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary order-1 sm:order-2"
                  disabled={uploading}
                >
                  {uploading
                    ? editingReport
                      ? 'Saving...'
                      : 'Submitting...'
                    : editingReport
                      ? 'Save Changes'
                      : 'Submit Report'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Success Modal for Report Submission */}
      {showSuccessModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 lg:p-4 z-50"
          onClick={e => {
            if (e.target === e.currentTarget) {
              setShowSuccessModal(false);
              setShowReportForm(false);
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setShowSuccessModal(false);
              setShowReportForm(false);
            }
          }}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="success-modal-title"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 lg:p-6">
              {/* Close Button */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setShowReportForm(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                  aria-label="Close success modal"
                >
                  ×
                </button>
              </div>

              {/* Success Content */}
              <div className="text-center">
                <div className="mb-6">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <svg
                      className="w-8 h-8 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h2 id="success-modal-title" className="text-2xl font-bold text-gray-900 mb-2">
                    Thank You!
                  </h2>
                  <p className="text-lg text-gray-700">
                    Your report has been submitted successfully.
                  </p>
                </div>

                {/* Reference Number */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-blue-900 mb-2">Your Reference Number</h3>
                  <p className="text-2xl font-mono font-bold text-blue-700">
                    SWARM{submittedReportId}
                  </p>
                  <p className="text-sm text-blue-600 mt-2">
                    Please save this reference number for future follow-up.
                  </p>
                </div>

                {/* Important Information */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-left">
                  <h3 className="font-semibold text-gray-900 mb-3">What happens next?</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>
                        <strong>Moderation:</strong> Your report will be reviewed by administrators
                        before appearing on the public map
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>
                        <strong>Privacy:</strong> Your personal details (name, email, phone) will
                        not be visible to the public
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>
                        <strong>Review Process:</strong> Reports are typically reviewed within 24-48
                        hours
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>
                        <strong>Follow-up:</strong> You can use your reference number (SWARM
                        {submittedReportId}) to inquire about your report
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setShowReportForm(false);
                  }}
                  className="btn btn-primary px-8 py-3 text-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Authentication Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 lg:p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 lg:p-6">
              {/* Close Button */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={closeAllModals}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                  aria-label="Close authentication modal"
                >
                  ×
                </button>
              </div>

              {/* Auth Form Content */}
              {renderAuth()}
            </div>
          </div>
        </div>
      )}

      {/* User Settings Modal */}
      <UserSettings
        user={user}
        isOpen={showUserSettings}
        onClose={() => setShowUserSettings(false)}
        api={api}
        onUserUpdate={updatedUser => setUser(updatedUser)}
      />
      <DesignThemeSwitcher />
    </div>
  );
}

export default App;
